import AdminGameSettings from "../features/games/AdminGameSettings";
import GameVisibilitySettings from "../features/games/GameVisibilitySettings";
import AdminAccessValidity from "../components/AdminAccessValidity";
import AdminLiveUsers from "../components/AdminLiveUsers";
import FreePlanSettings from "../components/FreePlanSettings";
import AdminWeeklySummarySettings from "../components/AdminWeeklySummarySettings";
import AdminSubscriptionReminderSettings from "../components/AdminSubscriptionReminderSettings";
import { FREE_DEFAULTS, FREE_LIMIT_FIELDS } from "../lib/freePlan";
import { WorkspaceNotificationSettings } from "../components/WorkspaceNotifications";
import WorkspaceBackups from "../components/WorkspaceBackups";
import WorkspacePlanFields from "../components/WorkspacePlanFields";
import { BASE_WORKSPACE_CAPABILITIES } from "../lib/workspaceCapabilities";
import { useState, useEffect } from "react";
import AdminPromotions from "../components/AdminPromotions";
import AdminWhatsAppQueue from "../components/AdminWhatsAppQueue";
import AdminCampaigns from "../components/AdminCampaigns";
import AdminPlanDistribution from "../components/AdminPlanDistribution";
import AdminPaymentFunnel from "../components/AdminPaymentFunnel";
import { useNavigate } from "react-router-dom";
import { api, useAuth, useTheme } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "@/lib/toast";
import {
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Moon,
  Sun,
  Search,
  Plus,
  Trash2,
  Package,
  Settings,
  Loader2,
  MessageSquare,
  Key,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  BookOpen,
  Edit,
  Copy,
  Save,
  Bell,
  Send,
  Eye,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Activity,
  Database,
  History,
  RotateCcw,
  HardDrive,
  Download,
  Upload,
  CreditCard,
  Check,
  Calendar,
  CalendarDays,
  Repeat,
  Wallet,
  Tv,
  Target,
  Plane,
  Rocket,
  Volume2,
  Image,
  Briefcase,
  Flame,
  ArrowRight,
  ShieldCheck
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from "recharts";

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, logout, setAppSettings: setGlobalAppSettings } = useAuth();
  const { darkMode, setDarkMode } = useTheme();
  
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [skuConfigs, setSkuConfigs] = useState([]);
  const [shopifyIntegration, setShopifyIntegration] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // SKU Form
  const [skuDialogOpen, setSkuDialogOpen] = useState(false);
  const [skuForm, setSkuForm] = useState({ sku: "", months: "", description: "", plan_id: "" });
  const [skuSubmitting, setSkuSubmitting] = useState(false);
  
  // App Settings
  const [appSettings, setAppSettings] = useState({ ...FREE_DEFAULTS, free_duration_scope: "new_users", trial_days: 0, daily_trial_limit: 5, portal_url: '', welcome_message: '', hidden_modules: [] });
  const [settingsSubmitting, setSettingsSubmitting] = useState(false);

  // Subscription management modal states
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subModalUser, setSubModalUser] = useState(null);
  const [subModalMode, setSubModalMode] = useState("plan"); // "plan" or "trial"
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [customTrialDays, setCustomTrialDays] = useState(7);
  const [subModalSubmitting, setSubModalSubmitting] = useState(false);
  
  const [paymentGateways, setPaymentGateways] = useState({
    mp_active: false,
    mp_access_token: "",
    mp_public_key: "",
    mp_webhook_secret: "",
    mp_test_mode: false,
    epayco_active: true,
    epayco_public_key: "",
    epayco_private_key: "",
    epayco_p_key: "",
    epayco_p_cust_id: "",
    epayco_test_mode: true
  });
  const [paymentGatewaysSubmitting, setPaymentGatewaysSubmitting] = useState(false);

  const toggleModule = (moduleId, active) => {
    const currentHidden = appSettings.hidden_modules || [];
    if (active) {
      setAppSettings({
        ...appSettings,
        hidden_modules: currentHidden.filter(id => id !== moduleId)
      });
    } else {
      if (!currentHidden.includes(moduleId)) {
        setAppSettings({
          ...appSettings,
          hidden_modules: [...currentHidden, moduleId]
        });
      }
    }
  };
  
  // Bot Button URL (WhatsApp short link)
  const [botButtonUrl, setBotButtonUrl] = useState('');
  const [botButtonLoading, setBotButtonLoading] = useState(false);
  
  // Meta Credentials Form
  const [metaCredentials, setMetaCredentials] = useState({
    whatsapp_api_token: "",
    whatsapp_phone_id: "",
    whatsapp_verify_token: "",
    whatsapp_otp_template: "",
    whatsapp_business_account_id: ""
  });
  const [metaDialogOpen, setMetaDialogOpen] = useState(false);
  const [metaSubmitting, setMetaSubmitting] = useState(false);
  const [metaTesting, setMetaTesting] = useState(false);
  const [metaTestResult, setMetaTestResult] = useState(null);
  
  // Knowledge Base State
  const [knowledgeEntries, setKnowledgeEntries] = useState([]);
  const [kbDialogOpen, setKbDialogOpen] = useState(false);
  const [kbForm, setKbForm] = useState({ title: "", content: "", category: "general" });
  const [kbSubmitting, setKbSubmitting] = useState(false);
  const [kbEditId, setKbEditId] = useState(null);
  
  // Pagination for users
  const [currentPage, setCurrentPage] = useState(1);
  const USERS_PER_PAGE = 10;
  
  // User detail modal
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsDays, setStatsDays] = useState(7);
  const [statsStartDate, setStatsStartDate] = useState("");
  const [statsEndDate, setStatsEndDate] = useState("");
  const [useCustomDates, setUseCustomDates] = useState(false);
  
  // Service usage stats
  const [serviceUsageStats, setServiceUsageStats] = useState(null);
  const [serviceUsageLoading, setServiceUsageLoading] = useState(false);
  
  // Payment notification settings
  const [paymentNotifSettings, setPaymentNotifSettings] = useState({
    enabled: false,
    template_name: "",
    hours_before: 12,
    template_text: "Hola, solo paso a recordarte que tu {tipo_pago} de {nombre} vence el {fecha_vencimiento}."
  });
  const [paymentNotifSubmitting, setPaymentNotifSubmitting] = useState(false);

  // Inactivity re-engagement sequence
  const [inactivitySettings, setInactivitySettings] = useState({
    enabled: false,
    version: 1,
    stages: [],
    stats: { accepted: 0, delivered: 0, failed: 0 },
    recent_deliveries: []
  });
  const [inactivitySubmitting, setInactivitySubmitting] = useState(false);
  const [inactivityRunning, setInactivityRunning] = useState(false);
  const [templateSubmitting, setTemplateSubmitting] = useState(null);

  // Streak reports and reminders
  const [streakSettings, setStreakSettings] = useState(null);
  const [streakReport, setStreakReport] = useState({ items: [], page: 1, pages: 0, total: 0, overview: {} });
  const [streakFilters, setStreakFilters] = useState({ search: "", exact_days: "", status: "", sort_by: "current_streak" });
  const [streakLoading, setStreakLoading] = useState(false);
  const [streakDetail, setStreakDetail] = useState(null);
  const [streakDetailOpen, setStreakDetailOpen] = useState(false);
  const [streakNotifications, setStreakNotifications] = useState({
    enabled: false,
    free_users_enabled: false,
    version: 1,
    stages: [],
    stats: { accepted: 0, delivered: 0, failed: 0, cancelled: 0 },
    recent_deliveries: []
  });
  const [streakNotificationSaving, setStreakNotificationSaving] = useState(false);
  const [streakNotificationRunning, setStreakNotificationRunning] = useState(false);
  const [streakTemplateSubmitting, setStreakTemplateSubmitting] = useState(null);
  const [streakReadiness, setStreakReadiness] = useState(null);
  const [streakPreview, setStreakPreview] = useState(null);
  const [streakPreviewLoading, setStreakPreviewLoading] = useState(false);

  // Backup System State
  const [backupStats, setBackupStats] = useState(null);
  const [userBackups, setUserBackups] = useState([]);
  const [selectedUserForBackup, setSelectedUserForBackup] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [backupToRestore, setBackupToRestore] = useState(null);
  const [backupSettings, setBackupSettings] = useState({ enabled: true, interval_days: 10, retention_days: 30 });
  const [backupSettingsSubmitting, setBackupSettingsSubmitting] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);

  // Event Templates State
  const [eventTemplates, setEventTemplates] = useState({
    owner_template: "",
    invitee_template: "",
    confirmation_template: ""
  });
  const [eventTemplatesSubmitting, setEventTemplatesSubmitting] = useState(false);

  // Habit Templates State
  const [habitTemplates, setHabitTemplates] = useState({
    single_template: "",
    multiple_template: ""
  });
  const [habitTemplatesSubmitting, setHabitTemplatesSubmitting] = useState(false);

  // Billing Plans State
  const [plans, setPlans] = useState([]);
  const [billingStats, setBillingStats] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planForm, setPlanForm] = useState({
    name: "",
    description: "",
    price: "",
    currency: "COP",
    billing_period: "monthly",
    payment_type: "one_time",  // one_time or recurring
    features: [],
    workspace_capabilities: { ...BASE_WORKSPACE_CAPABILITIES },
    is_active: true,
    trial_days: 14,
    sort_order: 0,
    is_featured: false,
    button_text: ""
  });
  const [planEditId, setPlanEditId] = useState(null);
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const [newFeature, setNewFeature] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/admin/login");
      return;
    }
    fetchData();
    fetchMetaCredentials();
    fetchAppSettings();
    fetchKnowledgeBase();
    fetchDashboardStats();
    fetchPaymentNotifSettings();
    fetchInactivitySettings();
    fetchStreakAdmin();
    fetchPaymentGateways();
    fetchBackupStats();
    fetchBackupSettings();
    fetchPlans();
    fetchEventTemplates();
    fetchHabitTemplates();
    fetchShopifyIntegration();
  // The bootstrap functions intentionally share this session boundary; the
  // filtered dashboards have their own refresh effects below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);
  
  // Refetch dashboard stats when days filter changes
  useEffect(() => {
    if (!loading) {
      fetchDashboardStats();
    }
  // These fetchers intentionally read the latest filter state listed below;
  // adding their render-local identity would refetch on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsDays, useCustomDates, statsStartDate, statsEndDate]);

  // Fetch service usage when stats change
  useEffect(() => {
    if (user?.role === "admin") {
      fetchServiceUsageStats();
    }
  // Same filter-bound request contract as the dashboard stats effect above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsDays, useCustomDates, statsStartDate, statsEndDate]);

  const fetchData = async () => {
    try {
      const [usersRes, metricsRes, skuRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/metrics"),
        api.get("/admin/sku-configs")
      ]);
      setUsers(usersRes.data);
      setMetrics(metricsRes.data);
      setSkuConfigs(skuRes.data);
    } catch (error) {
      toast.error("Error al cargar datos de admin");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMetaCredentials = async () => {
    try {
      const res = await api.get("/admin/meta-credentials");
      setMetaCredentials(res.data);
    } catch (error) {
      console.error("Error fetching meta credentials");
    }
  };
  
  const [freeDurationPreview, setFreeDurationPreview] = useState(null);

  const fetchAppSettings = async () => {
    try {
      const res = await api.get("/admin/app-settings");
      setAppSettings({ 
        ...FREE_DEFAULTS,
        ...Object.fromEntries(FREE_LIMIT_FIELDS.filter(([field]) => res.data[field] !== undefined).map(([field]) => [field, res.data[field]])),
        free_duration_scope: "new_users",
        trial_days: res.data.trial_days ?? 0,
        daily_trial_limit: res.data.daily_trial_limit !== undefined ? res.data.daily_trial_limit : 5,
        portal_url: res.data.portal_url || '',
        bot_button_url: res.data.bot_button_url || '',
        welcome_message: res.data.welcome_message || '',
        hidden_modules: res.data.hidden_modules || []
      });
      // Also set the bot button URL
      if (res.data.bot_button_url) {
        setBotButtonUrl(res.data.bot_button_url);
      }
    } catch (error) {
      console.error("Error fetching app settings");
    }
  };
  
  const fetchShopifyIntegration = async () => {
    try {
      const res = await api.get("/admin/shopify-integration");
      setShopifyIntegration(res.data.enabled);
    } catch (error) {
      console.error("Error fetching shopify integration:", error);
    }
  };

  const handleToggleShopifyIntegration = async (checked) => {
    try {
      await api.post("/admin/shopify-integration", { enabled: checked });
      setShopifyIntegration(checked);
      toast.success(checked ? "Integración de Shopify reactivada" : "Integración de Shopify suspendida");
    } catch (error) {
      toast.error("Error al actualizar integración de Shopify");
    }
  };
  
  const fetchPaymentGateways = async () => {
    try {
      const res = await api.get("/admin/payment-gateways");
      setPaymentGateways(res.data);
    } catch (error) {
      console.error("Error fetching payment gateways");
    }
  };
  
  const handleSavePaymentGateways = async () => {
    setPaymentGatewaysSubmitting(true);
    try {
      await api.put("/admin/payment-gateways", paymentGateways);
      toast.success("Pasarelas de pago actualizadas");
    } catch (error) {
      toast.error("Error al actualizar pasarelas");
    } finally {
      setPaymentGatewaysSubmitting(false);
    }
  };
  
  const fetchBotShortlink = async () => {
    try {
      setBotButtonLoading(true);
      const res = await api.get("/admin/create-portal-shortlink");
      if (res.data.success && res.data.shortlink) {
        setBotButtonUrl(res.data.shortlink);
        toast.success("Short link generado correctamente");
      } else {
        toast.error("No se pudo generar el short link");
      }
    } catch (error) {
      toast.error("Error al generar short link");
    } finally {
      setBotButtonLoading(false);
    }
  };
  
  const fetchKnowledgeBase = async () => {
    try {
      const res = await api.get("/admin/knowledge-base");
      setKnowledgeEntries(res.data);
    } catch (error) {
      console.error("Error fetching knowledge base");
    }
  };
  
  const fetchDashboardStats = async () => {
    setStatsLoading(true);
    try {
      let url = `/admin/dashboard-stats`;
      const cacheBuster = `_=${Date.now()}`;
      if (useCustomDates && statsStartDate && statsEndDate) {
        url += `?start_date=${statsStartDate}&end_date=${statsEndDate}&${cacheBuster}`;
      } else {
        url += `?days=${statsDays}&${cacheBuster}`;
      }
      const res = await api.get(url);
      setDashboardStats(res.data);
    } catch (error) {
      console.error("Error fetching dashboard stats");
    } finally {
      setStatsLoading(false);
    }
  };
  
  const fetchServiceUsageStats = async () => {
    setServiceUsageLoading(true);
    try {
      let url = `/admin/service-usage-stats`;
      const cacheBuster = `_=${Date.now()}`;
      if (useCustomDates && statsStartDate && statsEndDate) {
        url += `?start_date=${statsStartDate}&end_date=${statsEndDate}&${cacheBuster}`;
      } else {
        url += `?days=${statsDays}&${cacheBuster}`;
      }
      const res = await api.get(url);
      setServiceUsageStats(res.data);
    } catch (error) {
      console.error("Error fetching service usage stats");
    } finally {
      setServiceUsageLoading(false);
    }
  };
  
  const handleApplyFilters = () => {
    fetchDashboardStats();
    fetchServiceUsageStats();
  };
  
  const fetchUserDetail = async (userId) => {
    setUserDetailLoading(true);
    setUserDetailOpen(true);
    try {
      const res = await api.get(`/admin/user-detail/${userId}`);
      setSelectedUserDetail(res.data);
    } catch (error) {
      toast.error("Error al cargar detalles del usuario");
      setUserDetailOpen(false);
    } finally {
      setUserDetailLoading(false);
    }
  };
  
  const fetchPaymentNotifSettings = async () => {
    try {
      const res = await api.get("/admin/payment-notification-settings");
      setPaymentNotifSettings(res.data);
    } catch (error) {
      console.error("Error fetching payment notification settings");
    }
  };
  
  const handleSavePaymentNotifSettings = async () => {
    setPaymentNotifSubmitting(true);
    try {
      await api.put("/admin/payment-notification-settings", paymentNotifSettings);
      toast.success("Configuración de notificaciones guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setPaymentNotifSubmitting(false);
    }
  };

  const inactivityPayload = (settings = inactivitySettings) => ({
    enabled: Boolean(settings.enabled),
    stages: (settings.stages || []).map((stage) => ({
      id: stage.id,
      label: stage.label,
      enabled: stage.enabled !== false,
      delay_hours: Number(stage.delay_hours),
      template_name: stage.template_name,
      language: stage.language || "es",
      body_text: stage.body_text,
      button_text: stage.button_text || "Registrar gasto"
    }))
  });

  const fetchInactivitySettings = async () => {
    try {
      const res = await api.get("/admin/inactivity-notifications");
      setInactivitySettings(res.data);
    } catch (error) {
      console.error("Error fetching inactivity notification settings", error);
    }
  };

  const handleSaveInactivitySettings = async ({ silent = false } = {}) => {
    setInactivitySubmitting(true);
    try {
      const res = await api.put("/admin/inactivity-notifications", inactivityPayload());
      setInactivitySettings(res.data);
      if (!silent) toast.success("Secuencia de inactividad guardada");
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar la secuencia");
      return null;
    } finally {
      setInactivitySubmitting(false);
    }
  };

  const updateInactivityStage = (index, field, value) => {
    setInactivitySettings((current) => ({
      ...current,
      stages: current.stages.map((stage, stageIndex) => (
        stageIndex === index ? { ...stage, [field]: value } : stage
      ))
    }));
  };

  const addInactivityStage = () => {
    const stageNumber = (inactivitySettings.stages || []).length + 1;
    setInactivitySettings((current) => ({
      ...current,
      stages: [
        ...(current.stages || []),
        {
          id: `reminder_${Date.now()}`,
          label: `Recordatorio ${stageNumber}`,
          enabled: true,
          delay_hours: 48,
          template_name: `recordatorio_inactividad_etapa_${stageNumber}_personalizado`,
          language: "es",
          body_text: "Hola, llevas varios días sin registrar gastos. ¿Volviste a gastar en {{1}}? Cuéntame.",
          button_text: "Registrar gasto"
        }
      ]
    }));
  };

  const removeInactivityStage = (index) => {
    setInactivitySettings((current) => ({
      ...current,
      stages: current.stages.filter((_, stageIndex) => stageIndex !== index)
    }));
  };

  const handleCreateInactivityTemplate = async (stageId) => {
    setTemplateSubmitting(stageId);
    try {
      const saved = await handleSaveInactivitySettings({ silent: true });
      if (!saved) return;
      await api.post(`/admin/inactivity-notifications/templates/${stageId}`);
      toast.success("Plantilla enviada a Meta para aprobación");
      await fetchInactivitySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Meta no pudo crear la plantilla");
    } finally {
      setTemplateSubmitting(null);
    }
  };

  const handleRunInactivityNotifications = async () => {
    setInactivityRunning(true);
    try {
      const result = await api.post("/admin/inactivity-notifications/run");
      const { eligible = 0, due = 0, accepted = 0, failed = 0 } = result.data || {};
      toast.success(`Revisión completa: ${eligible} elegibles, ${due} vencidos, ${accepted} enviados, ${failed} fallidos`);
      await fetchInactivitySettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo ejecutar la revisión");
    } finally {
      setInactivityRunning(false);
    }
  };

  const fetchStreakReport = async ({ page = 1, filters = streakFilters } = {}) => {
    setStreakLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: "20",
        minimum_days: "0",
        sort_by: filters.sort_by || "current_streak"
      });
      if (filters.status) params.set("status", filters.status);
      if (filters.search?.trim()) params.set("search", filters.search.trim());
      if (filters.exact_days !== "" && filters.exact_days !== null) params.set("exact_days", String(Number(filters.exact_days) || 0));
      const response = await api.get(`/admin/streaks/report?${params.toString()}`);
      setStreakReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cargar el ranking de rachas");
    } finally {
      setStreakLoading(false);
    }
  };

  const fetchStreakAdmin = async () => {
    try {
      const [settingsResponse, notificationResponse, readinessResponse] = await Promise.all([
        api.get("/admin/streaks/settings"),
        api.get("/admin/streaks/notifications"),
        api.get("/admin/streaks/readiness")
      ]);
      setStreakSettings(settingsResponse.data);
      setStreakNotifications(notificationResponse.data);
      setStreakReadiness(readinessResponse.data);
      await fetchStreakReport({ page: 1 });
    } catch (error) {
      console.error("Error fetching streak administration", error);
    }
  };

  const handleStreakGlobalToggle = async (enabled) => {
    try {
      const response = await api.put("/admin/streaks/settings", {
        enabled,
        expected_version: streakSettings?.config_version
      });
      setStreakSettings(response.data);
      toast.success(enabled ? "Rachas activadas globalmente" : "Rachas pausadas; los días apagados quedarán congelados");
      const [readinessResponse] = await Promise.all([
        api.get("/admin/streaks/readiness"),
        fetchStreakReport({ page: 1 })
      ]);
      setStreakReadiness(readinessResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar el estado global");
      fetchStreakAdmin();
    }
  };

  const handleOpenStreakDetail = async (userId) => {
    setStreakDetailOpen(true);
    setStreakDetail(null);
    try {
      const response = await api.get(`/admin/streaks/users/${userId}?page=1&page_size=50`);
      setStreakDetail(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cargar el detalle de la racha");
      setStreakDetailOpen(false);
    }
  };

  const updateStreakStage = (index, field, value) => {
    setStreakNotifications((current) => ({
      ...current,
      stages: current.stages.map((stage, stageIndex) => (
        stageIndex === index ? { ...stage, [field]: value } : stage
      ))
    }));
  };

  const addStreakStage = () => {
    const number = (streakNotifications.stages || []).length + 1;
    setStreakNotifications((current) => ({
      ...current,
      stages: [...(current.stages || []), {
        id: `streak_reminder_${Date.now()}`,
        label: `Recordatorio ${number}`,
        enabled: true,
        timing_type: "hours_before_loss",
        hours: 1,
        template_name: `recordatorio_racha_${number}`,
        language: "es",
        body_text: "🔥 Llevas {{1}} días en racha. Te quedan {{2}} horas para completar tu minuto financiero de hoy.",
        button_text: "Completar minuto"
      }]
    }));
  };

  const removeStreakStage = (index) => {
    setStreakNotifications((current) => ({
      ...current,
      stages: current.stages.filter((_, stageIndex) => stageIndex !== index)
    }));
  };

  const handleSaveStreakNotifications = async () => {
    setStreakNotificationSaving(true);
    try {
      const payload = {
        enabled: Boolean(streakNotifications.enabled),
        free_users_enabled: Boolean(streakNotifications.free_users_enabled),
        expected_version: streakNotifications.version,
        stages: (streakNotifications.stages || []).map((stage) => ({
          id: stage.id,
          label: stage.label,
          enabled: stage.enabled !== false,
          timing_type: stage.timing_type,
          hours: Number(stage.hours),
          template_name: stage.template_name,
          language: stage.language || "es",
          body_text: stage.body_text,
          button_text: stage.button_text || "Completar minuto"
        }))
      };
      const response = await api.put("/admin/streaks/notifications", payload);
      setStreakNotifications(response.data);
      const readinessResponse = await api.get("/admin/streaks/readiness");
      setStreakReadiness(readinessResponse.data);
      toast.success("Recordatorios de racha guardados");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudieron guardar los recordatorios");
      return null;
    } finally {
      setStreakNotificationSaving(false);
    }
  };

  const handleSubmitStreakTemplate = async (stageId) => {
    setStreakTemplateSubmitting(stageId);
    try {
      const saved = await handleSaveStreakNotifications();
      if (!saved) return;
      await api.post(`/admin/streaks/notifications/templates/${stageId}`);
      toast.success("Plantilla de racha enviada a Meta para aprobación");
      const refreshed = await api.get("/admin/streaks/notifications");
      setStreakNotifications(refreshed.data);
      const readinessResponse = await api.get("/admin/streaks/readiness");
      setStreakReadiness(readinessResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Meta no pudo recibir la plantilla");
    } finally {
      setStreakTemplateSubmitting(null);
    }
  };

  const handleRunStreakNotifications = async () => {
    setStreakNotificationRunning(true);
    try {
      const response = await api.post("/admin/streaks/notifications/run");
      const result = response.data || {};
      toast.success(`Revisión completa: ${result.due || 0} pendientes, ${result.accepted || 0} aceptados, ${result.failed || 0} fallidos`);
      const refreshed = await api.get("/admin/streaks/notifications");
      setStreakNotifications(refreshed.data);
      const readinessResponse = await api.get("/admin/streaks/readiness");
      setStreakReadiness(readinessResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo ejecutar la revisión");
    } finally {
      setStreakNotificationRunning(false);
    }
  };

  const handlePreviewStreakNotifications = async () => {
    setStreakPreviewLoading(true);
    try {
      const response = await api.post("/admin/streaks/notifications/preview");
      setStreakPreview(response.data);
      toast.success(`Simulación: ${response.data?.would_send || 0} envíos posibles, sin enviar mensajes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo simular la revisión");
    } finally {
      setStreakPreviewLoading(false);
    }
  };

  // Event Templates handlers
  const fetchEventTemplates = async () => {
    try {
      const res = await api.get("/admin/event-templates");
      setEventTemplates(res.data || { owner_template: "", invitee_template: "", confirmation_template: "" });
    } catch (error) {
      console.error("Error fetching event templates:", error);
    }
  };

  const handleSaveEventTemplates = async () => {
    setEventTemplatesSubmitting(true);
    try {
      await api.post("/admin/event-templates", eventTemplates);
      toast.success("Plantillas de eventos guardadas");
    } catch (error) {
      toast.error("Error al guardar plantillas");
    } finally {
      setEventTemplatesSubmitting(false);
    }
  };

  // Habit Templates handlers
  const fetchHabitTemplates = async () => {
    try {
      const res = await api.get("/admin/habit-templates");
      setHabitTemplates(res.data || { single_template: "", multiple_template: "" });
    } catch (error) {
      console.error("Error fetching habit templates:", error);
    }
  };

  const handleSaveHabitTemplates = async () => {
    setHabitTemplatesSubmitting(true);
    try {
      await api.post("/admin/habit-templates", habitTemplates);
      toast.success("Plantilla de hábitos guardada");
    } catch (error) {
      toast.error("Error al guardar plantilla");
    } finally {
      setHabitTemplatesSubmitting(false);
    }
  };
  
  const handleUpdateAppSettings = async () => {
    if (!Number.isInteger(appSettings.trial_days) || appSettings.trial_days < 0 || appSettings.trial_days > 36500 || FREE_LIMIT_FIELDS.some(([field]) => appSettings[field] !== null && (!Number.isInteger(appSettings[field]) || appSettings[field] < 0))) {
      toast.error("Ingresa cantidades enteras mayores o iguales a 0."); return;
    }
    if (appSettings.free_duration_scope === "all_free_users" && freeDurationPreview?.trial_days !== appSettings.trial_days) {
      toast.error("Consulta la vista previa del impacto antes de guardar la duración para usuarios existentes."); return;
    }
    setSettingsSubmitting(true);
    try {
      await api.put("/admin/app-settings", appSettings);
      // Update global context so Dashboard UI re-renders synchronously
      if (setGlobalAppSettings) {
        setGlobalAppSettings(prev => ({
          ...prev,
          hidden_modules: appSettings.hidden_modules
        }));
      }
      setFreeDurationPreview(null);
      setAppSettings(prev => ({ ...prev, free_duration_scope: "new_users" }));
      toast.success("Configuración actualizada");
    } catch (error) {
      toast.error("Error al actualizar configuración");
    } finally {
      setSettingsSubmitting(false);
    }
  };
  
  const handleUpdateMetaCredentials = async () => {
    setMetaSubmitting(true);
    try {
      await api.put("/admin/meta-credentials", metaCredentials);
      toast.success("Credenciales de Meta actualizadas");
      setMetaDialogOpen(false);
      fetchMetaCredentials();
    } catch (error) {
      toast.error("Error al actualizar credenciales");
    } finally {
      setMetaSubmitting(false);
    }
  };
  
  const handleTestMetaCredentials = async () => {
    setMetaTesting(true);
    setMetaTestResult(null);
    try {
      const res = await api.post("/admin/test-whatsapp-credentials");
      setMetaTestResult(res.data);
      if (res.data.token_valid) {
        toast.success("Credenciales válidas");
      } else {
        toast.error("Credenciales inválidas");
      }
    } catch (error) {
      toast.error("Error al probar credenciales");
    } finally {
      setMetaTesting(false);
    }
  };

  // Knowledge Base handlers
  const handleOpenKbDialog = (entry = null) => {
    if (entry) {
      setKbEditId(entry.id);
      setKbForm({ title: entry.title, content: entry.content, category: entry.category || "general" });
    } else {
      setKbEditId(null);
      setKbForm({ title: "", content: "", category: "general" });
    }
    setKbDialogOpen(true);
  };

  const handleSaveKnowledgeEntry = async (e) => {
    e.preventDefault();
    if (!kbForm.title || !kbForm.content) {
      toast.error("Título y contenido son requeridos");
      return;
    }

    setKbSubmitting(true);
    try {
      if (kbEditId) {
        await api.put(`/admin/knowledge-base/${kbEditId}`, kbForm);
        toast.success("Entrada actualizada");
      } else {
        await api.post("/admin/knowledge-base", kbForm);
        toast.success("Entrada creada");
      }
      setKbDialogOpen(false);
      setKbForm({ title: "", content: "", category: "general" });
      setKbEditId(null);
      fetchKnowledgeBase();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo guardar la entrada. Verifica la conexión e inténtalo de nuevo.");
    } finally {
      setKbSubmitting(false);
    }
  };

  const handleDeleteKnowledgeEntry = async (entryId) => {
    try {
      await api.delete(`/admin/knowledge-base/${entryId}`);
      toast.success("Entrada eliminada");
      fetchKnowledgeBase();
    } catch (error) {
      toast.error("Error al eliminar entrada");
    }
  };

  // Reminder Settings handlers
  const handleUpdateUser = async (userId, updates) => {
    try {
      await api.put(`/admin/users/${userId}`, updates);
      toast.success("Usuario actualizado");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar usuario");
    }
  };

  const openSubscriptionModal = (u) => {
    setSubModalUser(u);
    setSelectedPlanId(u.plan_id || "clear");
    setCustomTrialDays(7);
    setSubModalMode("plan");
    setSubModalOpen(true);
  };

  const handleSaveSubscription = async () => {
    if (!subModalUser) return;
    setSubModalSubmitting(true);
    try {
      if (subModalMode === "plan") {
        if (!selectedPlanId) {
          toast.error("Por favor selecciona un plan o la opción de limpiar.");
          setSubModalSubmitting(false);
          return;
        }
        await api.put(`/admin/users/${subModalUser.id}`, { plan_id: selectedPlanId });
        toast.success("Suscripción del usuario actualizada exitosamente");
      } else {
        if (!Number.isInteger(customTrialDays) || customTrialDays < 0) {
          toast.error("Ingresa un número entero de días mayor o igual a 0.");
          setSubModalSubmitting(false);
          return;
        }
        await api.put(`/admin/users/${subModalUser.id}`, { trial_days: customTrialDays });
        toast.success("Acceso gratuito del usuario actualizado");
      }
      setSubModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar suscripción");
    } finally {
      setSubModalSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId, phone) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`Usuario ${phone} eliminado completamente`);
      fetchData();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Error al eliminar usuario");
      }
    }
  };

  const handleAddSku = async (e) => {
    e.preventDefault();
    if (!skuForm.sku || !skuForm.months) {
      toast.error("SKU y meses son requeridos");
      return;
    }

    setSkuSubmitting(true);
    try {
      await api.post("/admin/sku-configs", {
        sku: skuForm.sku.toUpperCase(),
        months: parseInt(skuForm.months),
        description: skuForm.description || null,
        plan_id: skuForm.plan_id || null
      });
      toast.success("SKU configurado");
      setSkuDialogOpen(false);
      setSkuForm({ sku: "", months: "", description: "", plan_id: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al configurar SKU");
    } finally {
      setSkuSubmitting(false);
    }
  };

  const handleDeleteSku = async (sku) => {
    try {
      await api.delete(`/admin/sku-configs/${sku}`);
      toast.success("SKU eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar SKU");
    }
  };

  // ============== BACKUP FUNCTIONS ==============
  const fetchBackupSettings = async () => {
    try {
      const res = await api.get("/admin/backup-settings");
      setBackupSettings(res.data);
    } catch (error) {
      console.error("Error fetching backup settings:", error);
    }
  };

  const handleSaveBackupSettings = async () => {
    setBackupSettingsSubmitting(true);
    try {
      await api.post("/admin/backup-settings", backupSettings);
      toast.success("Configuración de backups guardada exitosamente");
    } catch (error) {
      toast.error("Error al guardar la configuración");
    } finally {
      setBackupSettingsSubmitting(false);
    }
  };

  const handleRunBackupCleanup = async () => {
    try {
      const res = await api.post("/admin/backups/cleanup-now");
      toast.success(`Limpieza completada: ${res.data.deleted_count} backups antiguos eliminados`);
      fetchBackupStats();
    } catch (error) {
      toast.error("Error al ejecutar limpieza de backups");
    }
  };

  const fetchBackupStats = async () => {
    try {
      const res = await api.get("/admin/backups/stats");
      setBackupStats(res.data);
    } catch (error) {
      console.error("Error fetching backup stats:", error);
    }
  };

  const fetchUserBackups = async (userId) => {
    setBackupLoading(true);
    try {
      const res = await api.get(`/admin/backups/user/${userId}`);
      setUserBackups(res.data.backups || []);
      setSelectedUserForBackup(res.data.user);
      setBackupDialogOpen(true);
    } catch (error) {
      toast.error("Error al cargar backups del usuario");
    } finally {
      setBackupLoading(false);
    }
  };

  const handleCreateBackup = async (userId) => {
    try {
      const res = await api.post(`/admin/backups/create/${userId}`);
      toast.success(`Backup creado: ${res.data.summary?.transactions || 0} transacciones guardadas`);
      fetchUserBackups(userId);
      fetchBackupStats();
    } catch (error) {
      toast.error("Error al crear backup");
    }
  };

  const handleRestoreBackup = async () => {
    if (!backupToRestore) return;
    
    try {
      const res = await api.post(`/admin/backups/restore/${backupToRestore.backup_id}`);
      toast.success("Backup restaurado exitosamente");
      setRestoreConfirmOpen(false);
      setBackupToRestore(null);
      fetchUserBackups(backupToRestore.user_id);
    } catch (error) {
      toast.error("Error al restaurar backup");
    }
  };

  const handleDeleteBackup = async (backupId, userId) => {
    try {
      await api.delete(`/admin/backups/${backupId}`);
      toast.success("Backup eliminado");
      fetchUserBackups(userId);
      fetchBackupStats();
    } catch (error) {
      toast.error("Error al eliminar backup");
    }
  };

  const handleRunAllBackups = async () => {
    setRunningBackup(true);
    try {
      const res = await api.post("/admin/backups/run-now");
      toast.success(`Backup completado: ${res.data.created_count} usuarios respaldados`);
      fetchBackupStats();
    } catch (error) {
      toast.error("Error al ejecutar backup masivo");
    } finally {
      setRunningBackup(false);
    }
  };

  // ============ BILLING PLANS FUNCTIONS ============
  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const [plansRes, statsRes] = await Promise.all([
        api.get("/billing/admin/plans"),
        api.get("/billing/admin/stats")
      ]);
      setPlans(plansRes.data.plans || []);
      setBillingStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setPlansLoading(false);
    }
  };

  const handleOpenPlanDialog = (plan = null) => {
    if (plan) {
      setPlanEditId(plan.id);
      setPlanForm({
        name: plan.name || "",
        description: plan.description || "",
        price: String(plan.price || ""),
        currency: plan.currency || "COP",
        billing_period: plan.billing_period || "monthly",
        payment_type: plan.payment_type || "one_time",
        features: plan.features || [],
        workspace_capabilities: { ...BASE_WORKSPACE_CAPABILITIES, ...plan.workspace_capabilities },
        is_active: plan.is_active !== false,
        trial_days: plan.trial_days || 0,
        sort_order: plan.sort_order || 0,
        is_featured: plan.is_featured || false,
        button_text: plan.button_text || ""
      });
    } else {
      setPlanEditId(null);
      setPlanForm({
        name: "",
        description: "",
        price: "",
        currency: "COP",
        billing_period: "monthly",
        payment_type: "one_time",
        features: [],
    workspace_capabilities: { ...BASE_WORKSPACE_CAPABILITIES },
        is_active: true,
        trial_days: 14,
        sort_order: plans.length,
        is_featured: false,
        button_text: ""
      });
    }
    setPlanDialogOpen(true);
  };

  const handleDuplicatePlan = (plan) => {
    setPlanEditId(null);
    setPlanForm({
      name: `${plan.name || "Plan"} (Copia)`,
      description: plan.description || "",
      price: String(plan.price || ""),
      currency: plan.currency || "COP",
      billing_period: plan.billing_period || "monthly",
      payment_type: plan.payment_type || "one_time",
      features: plan.features ? [...plan.features] : [],
      workspace_capabilities: { ...BASE_WORKSPACE_CAPABILITIES, ...plan.workspace_capabilities },
      is_active: plan.is_active !== false,
      trial_days: plan.trial_days || 0,
      sort_order: plans.length,
      is_featured: false,
      button_text: plan.button_text || ""
    });
    setPlanDialogOpen(true);
    toast.info("Plan duplicado. Puedes ajustar los campos y guardarlo.");
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setPlanForm(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature("");
    }
  };

  const handleRemoveFeature = (index) => {
    setPlanForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSavePlan = async () => {
    if (!planForm.name || !planForm.price) {
      toast.error("Nombre y precio son requeridos");
      return;
    }

    setPlanSubmitting(true);
    try {
      const payload = {
        ...planForm,
        price: parseFloat(planForm.price)
      };

      if (planEditId) {
        await api.put(`/billing/admin/plans/${planEditId}`, payload);
        toast.success("Plan actualizado");
      } else {
        await api.post("/billing/admin/plans", payload);
        toast.success("Plan creado");
      }
      setPlanDialogOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error("Error al guardar plan");
    } finally {
      setPlanSubmitting(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await api.delete(`/billing/admin/plans/${planId}`);
      toast.success("Plan eliminado");
      fetchPlans();
    } catch (error) {
      toast.error("Error al eliminar plan");
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('es-CO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredUsers = users.filter(u => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const matchesSearch = !normalizedSearch || [
      u.phone,
      u.email,
      u.name,
      u.whatsapp_username,
      u.whatsapp_display_name,
    ].some(value => String(value || "").toLowerCase().includes(normalizedSearch));
    const matchesStatus = statusFilter === "all" || u.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );
  
  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className="min-h-screen aorra-admin-shell p-4 md:p-6">
        <div className="aorra-admin-main max-w-7xl mx-auto space-y-6 animate-pulse">
          <div className="h-9 w-56 bg-blue-100 rounded-2xl"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1,2,3,4].map((i) => (
              <div key={i} className="h-36 bg-white rounded-[24px] border border-blue-50"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen aorra-admin-shell" data-testid="admin-page">
      {/* Header */}
      <header className="aorra-admin-header sticky top-0 z-50">
        <div className="aorra-admin-header-inner max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="aorra-admin-brand-mark">
              <img src="/logo-claro.png" alt="Aorra" />
            </div>
            <div>
              <span className="aorra-admin-brand-title">Aorra Admin</span>
              <p className="aorra-admin-brand-subtitle">Panel de administración</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="aorra-admin-icon-button"
              aria-label={darkMode ? "Activar tema claro" : "Activar tema oscuro"}
            >
              {darkMode ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4" />}
            </button>
            <Button className="aorra-admin-logout" variant="outline" onClick={() => { logout(); navigate("/admin/login"); }}>
              <span className="hidden sm:inline">Cerrar sesión</span>
              <span className="sm:hidden">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="aorra-admin-main max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
        <section className="aorra-admin-intro">
          <div>
            <p className="aorra-admin-eyebrow">CENTRO DE CONTROL</p>
            <h1>Hola, {user?.name?.split(" ")[0] || "Administrador"}</h1>
            <p>Consulta la actividad de Aorra y gestiona cada sección desde un solo lugar.</p>
          </div>
          <div className="aorra-admin-role-pill">
            <ShieldCheck className="w-4 h-4" />
            Administración
          </div>
        </section>

        {/* Metrics Cards - Always visible */}
        <div className="aorra-admin-kpis grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AdminLiveUsers />

          <Card className="aorra-admin-kpi aorra-admin-kpi--active stat-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="aorra-admin-kpi-icon"><TrendingUp className="w-5 h-5" /></span>
                <span className="aorra-admin-kpi-label">Usuarios activos</span>
              </div>
              <h3 className="aorra-admin-kpi-value" data-testid="active-users">
                {metrics?.active_users || 0}
              </h3>
              <p className="aorra-admin-kpi-note">
                {metrics?.trial_users || 0} en período de prueba
              </p>
            </CardContent>
          </Card>

          <Card className="aorra-admin-kpi aorra-admin-kpi--revenue stat-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="aorra-admin-kpi-icon"><DollarSign className="w-5 h-5" /></span>
                <span className="aorra-admin-kpi-label">MRR</span>
              </div>
              <h3 className="aorra-admin-kpi-value" data-testid="mrr">
                {formatCurrency(metrics?.mrr || 0)}
              </h3>
              <p className="aorra-admin-kpi-note">
                Ingresos recurrentes mensuales
              </p>
            </CardContent>
          </Card>

          <Card className="aorra-admin-kpi aorra-admin-kpi--churn stat-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="aorra-admin-kpi-icon"><TrendingDown className="w-5 h-5" /></span>
                <span className="aorra-admin-kpi-label">Churn rate</span>
              </div>
              <h3 className="aorra-admin-kpi-value" data-testid="churn">
                {metrics?.churn_rate || 0}%
              </h3>
              <p className="aorra-admin-kpi-note">
                Tasa de cancelación
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Navigation */}
        <Tabs defaultValue="users" className="aorra-admin-tabs space-y-6">
          <TabsList className="aorra-admin-tabs-list w-full">
            <TabsTrigger value="promotions" className="flex items-center gap-2" data-testid="tab-promotions" aria-label="Referidos y descuentos">
              <Users className="w-4 h-4" /><span className="hidden sm:inline">Referidos</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" onClick={() => navigate("/admin/analytics")} className="flex items-center gap-2" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Analíticas</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2" data-testid="tab-users">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Usuarios</span>
            </TabsTrigger>
            <TabsTrigger value="plans" className="flex items-center gap-2" data-testid="tab-plans">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Planes</span>
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="flex items-center gap-2"><BarChart3 className="w-4 h-4" />Campañas</TabsTrigger>
            <TabsTrigger value="whatsapp-queue" className="flex items-center gap-2"><MessageSquare className="w-4 h-4" />Cola WhatsApp</TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2" data-testid="tab-notifications">
              <Bell className="w-4 h-4" />
              <span className="hidden sm:inline">Notificaciones</span>
            </TabsTrigger>
            <TabsTrigger value="streaks" className="flex items-center gap-2" data-testid="tab-streaks">
              <Flame className="w-4 h-4" />
              <span className="hidden sm:inline">Rachas</span>
            </TabsTrigger>
            <TabsTrigger value="games" className="flex items-center gap-2"><Rocket className="w-4 h-4" />Juego</TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2" data-testid="tab-config">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Configuración</span>
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-2" data-testid="tab-integrations">
              <Key className="w-4 h-4" />
              <span className="hidden sm:inline">Integraciones</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2" data-testid="tab-payments">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Pasarelas</span>
            </TabsTrigger>
            <TabsTrigger value="backups" className="flex items-center gap-2" data-testid="tab-backups">
              <Database className="w-4 h-4" />
              <span className="hidden sm:inline">Backups</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Payments Tab */}
          <TabsContent value="games"><AdminGameSettings api={api} /></TabsContent>
          <TabsContent value="campaigns"><AdminCampaigns /></TabsContent>
          <TabsContent value="whatsapp-queue"><AdminWhatsAppQueue /></TabsContent>
          <TabsContent value="promotions"><AdminPromotions /></TabsContent>
          <TabsContent value="payments" className="space-y-6">
            <Card className="chart-container border-2 border-primary/20">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <CreditCard className="w-6 h-6 text-primary" />
                  <div>
                    <CardTitle className="font-['Outfit'] text-primary">Configuración de Pasarelas de Pago</CardTitle>
                    <p className="text-sm text-on-surface-variant">
                      Administra las credenciales y activa/desactiva las opciones de pago.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Mercado Pago Section */}
                <div className="space-y-4 bg-slate-50 p-6 rounded-xl border border-slate-100">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                      Mercado Pago
                    </h3>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="mp_active" className="text-sm font-medium">Activo</Label>
                      <Switch 
                        id="mp_active"
                        checked={paymentGateways.mp_active}
                        onCheckedChange={(checked) => setPaymentGateways({...paymentGateways, mp_active: checked})}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label>Public Key</Label>
                      <Input 
                        value={paymentGateways.mp_public_key}
                        onChange={(e) => setPaymentGateways({...paymentGateways, mp_public_key: e.target.value})}
                        placeholder="APP_USR-..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Access Token</Label>
                      <Input 
                        type="password"
                        value={paymentGateways.mp_access_token}
                        onChange={(e) => setPaymentGateways({...paymentGateways, mp_access_token: e.target.value})}
                        placeholder="APP_USR-..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mp-webhook-secret">Clave secreta de Webhooks</Label>
                      <Input id="mp-webhook-secret" type="password" autoComplete="new-password"
                        value={paymentGateways.mp_webhook_secret || ""}
                        onChange={(event) => setPaymentGateways({...paymentGateways, mp_webhook_secret: event.target.value})} />
                      <p className="text-xs text-slate-500">En Mercado Pago, configura Pagos y Suscripciones en Webhooks con la URL {`${api.defaults?.baseURL || "https://api.aorra.online/api"}/billing/webhook`}. Copia aquí la clave secreta que genere.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch id="mp-test-mode" checked={Boolean(paymentGateways.mp_test_mode)}
                        onCheckedChange={(checked) => setPaymentGateways({...paymentGateways, mp_test_mode: checked})} />
                      <Label htmlFor="mp-test-mode">Entorno de pruebas de Mercado Pago</Label>
                    </div>
                    <p className="text-xs text-slate-500">Usa credenciales y compradores de prueba en un entorno de pruebas separado. Desactiva esta opción para cobrar con tus credenciales de producción.</p>
                  </div>
                </div>

                {/* ePayco Section */}
                <div className="space-y-4 bg-slate-50 p-6 rounded-xl border border-slate-100">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                      ePayco
                    </h3>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="epayco_active" className="text-sm font-medium">Activo</Label>
                      <Switch 
                        id="epayco_active"
                        checked={paymentGateways.epayco_active}
                        onCheckedChange={(checked) => setPaymentGateways({...paymentGateways, epayco_active: checked})}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <Label htmlFor="epayco_test_mode" className="text-sm font-medium text-slate-500">Modo de Pruebas (Test Mode)</Label>
                    <Switch 
                      id="epayco_test_mode"
                      checked={paymentGateways.epayco_test_mode}
                      onCheckedChange={(checked) => setPaymentGateways({...paymentGateways, epayco_test_mode: checked})}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Public Key</Label>
                      <Input 
                        value={paymentGateways.epayco_public_key}
                        onChange={(e) => setPaymentGateways({...paymentGateways, epayco_public_key: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Private Key</Label>
                      <Input 
                        type="password"
                        value={paymentGateways.epayco_private_key}
                        onChange={(e) => setPaymentGateways({...paymentGateways, epayco_private_key: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>P_KEY</Label>
                      <Input 
                        type="password"
                        value={paymentGateways.epayco_p_key}
                        onChange={(e) => setPaymentGateways({...paymentGateways, epayco_p_key: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>P_CUST_ID_CLIENTE</Label>
                      <Input 
                        value={paymentGateways.epayco_p_cust_id}
                        onChange={(e) => setPaymentGateways({...paymentGateways, epayco_p_cust_id: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleSavePaymentGateways} 
                  disabled={paymentGatewaysSubmitting}
                  className="w-full btn-primary py-6 text-lg"
                >
                  {paymentGatewaysSubmitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
                  Guardar Pasarelas de Pago
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Dashboard Stats Section */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <CardTitle className="font-['Outfit'] flex items-center gap-2">
                      <Activity className="w-5 h-5 text-cyan-600" />
                      Estadísticas de Uso
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={useCustomDates}
                        onCheckedChange={setUseCustomDates}
                        id="custom-dates"
                      />
                      <Label htmlFor="custom-dates" className="text-sm">Fechas específicas</Label>
                    </div>
                  </div>
                  
                  {/* Date filters */}
                  <div className="flex flex-wrap items-center gap-3">
                    {useCustomDates ? (
                      <>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500">Desde:</Label>
                          <Input
                            type="date"
                            value={statsStartDate}
                            onChange={(e) => setStatsStartDate(e.target.value)}
                            className="w-[150px]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500">Hasta:</Label>
                          <Input
                            type="date"
                            value={statsEndDate}
                            onChange={(e) => setStatsEndDate(e.target.value)}
                            className="w-[150px]"
                          />
                        </div>
                        <Button 
                          size="sm" 
                          onClick={handleApplyFilters}
                          disabled={!statsStartDate || !statsEndDate}
                          className="gap-2"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Aplicar
                        </Button>
                      </>
                    ) : (
                      <Select value={String(statsDays)} onValueChange={(v) => setStatsDays(Number(v))}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Período" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">Últimos 7 días</SelectItem>
                          <SelectItem value="14">Últimos 14 días</SelectItem>
                          <SelectItem value="30">Últimos 30 días</SelectItem>
                          <SelectItem value="90">Últimos 90 días</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {/* Manual refresh button */}
                    {!useCustomDates && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleApplyFilters}
                        className="gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Actualizar
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
                  </div>
                ) : dashboardStats ? (
                  <div className="space-y-6">
                    {/* Stats summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                        <p className="text-xs text-slate-500 uppercase font-bold">Usuarios Activos</p>
                        <p className="text-2xl font-bold text-emerald-600">{dashboardStats.totals?.active_users_period || 0}</p>
                        <p className="text-xs text-slate-400">en el período</p>
                      </div>
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                        <p className="text-xs text-slate-500 uppercase font-bold">Usuarios Inactivos</p>
                        <p className="text-2xl font-bold text-orange-600">{dashboardStats.totals?.inactive_users_period || 0}</p>
                        <p className="text-xs text-slate-400">en el período</p>
                      </div>
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                        <p className="text-xs text-slate-500 uppercase font-bold">Nuevos Usuarios</p>
                        <p className="text-2xl font-bold text-cyan-600">{dashboardStats.totals?.new_users_period || 0}</p>
                        <p className="text-xs text-slate-400">registrados</p>
                      </div>
                      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                        <p className="text-xs text-slate-500 uppercase font-bold">Transacciones</p>
                        <p className="text-2xl font-bold text-purple-600">{dashboardStats.totals?.total_transactions_period || 0}</p>
                        <p className="text-xs text-slate-400">en el período</p>
                      </div>
                    </div>
                    
                    <AdminPlanDistribution data={dashboardStats.plan_distribution} />

                    {/* Averages */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 border rounded-lg text-center">
                        <p className="text-xs text-slate-500">Promedio Diario Txs</p>
                        <p className="text-xl font-bold">{dashboardStats.averages?.daily_transactions?.toFixed(1) || 0}</p>
                      </div>
                      <div className="p-3 border rounded-lg text-center">
                        <p className="text-xs text-slate-500">DAU Promedio</p>
                        <p className="text-xl font-bold">{dashboardStats.averages?.daily_active_users?.toFixed(1) || 0}</p>
                      </div>
                      <div className="p-3 border rounded-lg text-center">
                        <p className="text-xs text-slate-500">Txs por Usuario Activo</p>
                        <p className="text-xl font-bold">{dashboardStats.averages?.transactions_per_active_user?.toFixed(1) || 0}</p>
                      </div>
                    </div>

                    {/* Daily Chart */}
                    {dashboardStats.daily_breakdown?.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Actividad Diaria</p>
                          {dashboardStats?.period && (
                            <span className="text-xs text-cyan-600 font-medium">
                              {(() => {
                                const startParts = dashboardStats.period.start.split('T')[0].split('-');
                                const endParts = dashboardStats.period.end.split('T')[0].split('-');
                                const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
                                const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
                                return `${startDate.toLocaleDateString('es-CO')} - ${endDate.toLocaleDateString('es-CO')}`;
                              })()}
                            </span>
                          )}
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={dashboardStats.daily_breakdown}>
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 10 }}
                              tickFormatter={(value) => {
                                // Parse date without timezone conversion issues
                                const [year, month, day] = value.split('-').map(Number);
                                const d = new Date(year, month - 1, day);
                                return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
                              }}
                            />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip 
                              labelFormatter={(value) => {
                                const [year, month, day] = value.split('-').map(Number);
                                const d = new Date(year, month - 1, day);
                                return d.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
                              }}
                              contentStyle={{ fontSize: '12px' }}
                            />
                            <Bar dataKey="transactions" fill="#0891b2" name="Transacciones" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="active_users" fill="#10b981" name="Usuarios Activos" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    
                    {/* User Status Summary */}
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                      <div className="text-center">
                        <p className="text-xs text-slate-500 uppercase">En Trial</p>
                        <p className="text-lg font-bold text-blue-600">{dashboardStats.totals?.trial_users || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500 uppercase">Suscripción Activa</p>
                        <p className="text-lg font-bold text-emerald-600">{dashboardStats.totals?.active_subscription_users || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500 uppercase">Expirados</p>
                        <p className="text-lg font-bold text-rose-600">{dashboardStats.totals?.expired_users || 0}</p>
                      </div>
                    </div>

                    {/* Behavior Stats */}
                    {dashboardStats.behavior && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                        {/* Input Type Stats */}
                        <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                          <p className="text-sm font-medium mb-4 text-slate-700 dark:text-slate-300">Entrada de Datos (WhatsApp)</p>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm flex items-center gap-2 text-slate-600 dark:text-slate-400">
                                <MessageSquare className="w-4 h-4 text-blue-500" /> Mensajes de Texto
                              </span>
                              <span className="font-bold text-slate-800 dark:text-slate-200">{dashboardStats.behavior.input_stats.text}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm flex items-center gap-2 text-slate-600 dark:text-slate-400">
                                <Volume2 className="w-4 h-4 text-purple-500" /> Notas de Voz
                              </span>
                              <span className="font-bold text-slate-800 dark:text-slate-200">{dashboardStats.behavior.input_stats.audio}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm flex items-center gap-2 text-slate-600 dark:text-slate-400">
                                <Image className="w-4 h-4 text-green-500" /> Fotos / Facturas
                              </span>
                              <span className="font-bold text-slate-800 dark:text-slate-200">{dashboardStats.behavior.input_stats.image}</span>
                            </div>
                          </div>
                        </div>

                        {/* Chat Mode Stats */}
                        <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                          <p className="text-sm font-medium mb-4 text-slate-700 dark:text-slate-300">Personalidad (Chat Mode)</p>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm flex items-center gap-2 text-slate-600 dark:text-slate-400">
                                <Briefcase className="w-4 h-4 text-slate-500" /> Modo Serio
                              </span>
                              <span className="font-bold text-slate-800 dark:text-slate-200">{dashboardStats.behavior.chat_modes.serious}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm flex items-center gap-2 text-slate-600 dark:text-slate-400">
                                <Flame className="w-4 h-4 text-orange-500" /> Modo Sarcástico
                              </span>
                              <span className="font-bold text-slate-800 dark:text-slate-200">{dashboardStats.behavior.chat_modes.roast}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <AdminPaymentFunnel data={dashboardStats.funnel} period={dashboardStats.period} />

                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No hay datos disponibles</p>
                )}
              </CardContent>
            </Card>

            {/* Cross-channel service usage */}
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  Uso por funcionalidad y canal
                </CardTitle>
                <p className="text-xs text-slate-500">
                  Acciones realizadas por los usuarios desde WhatsApp y el portal
                  {serviceUsageStats?.period && (
                    <span className="ml-1 text-purple-600 font-medium">
                      ({(() => {
                        const startParts = serviceUsageStats.period.start.split('T')[0].split('-');
                        const endParts = serviceUsageStats.period.end.split('T')[0].split('-');
                        const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
                        const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
                        return `${startDate.toLocaleDateString('es-CO')} - ${endDate.toLocaleDateString('es-CO')}`;
                      })()})
                    </span>
                  )}
                </p>
              </CardHeader>
              <CardContent>
                {serviceUsageLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : serviceUsageStats && serviceUsageStats.usage?.length > 0 ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Uso total</p>
                        <p className="mt-1 text-3xl font-bold text-slate-900 dark:text-white">{serviceUsageStats.total_actions?.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">acciones en las 5 funcionalidades</p>
                      </div>
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800/50 dark:bg-emerald-900/20">
                        <p className="text-xs font-medium uppercase tracking-wide text-emerald-700 dark:text-emerald-300">WhatsApp</p>
                        <p className="mt-1 text-3xl font-bold text-emerald-700 dark:text-emerald-300">{(serviceUsageStats.channel_totals?.whatsapp || 0).toLocaleString()}</p>
                        <p className="text-xs text-emerald-700/70 dark:text-emerald-300/70">
                          {serviceUsageStats.total_actions > 0 ? Math.round(((serviceUsageStats.channel_totals?.whatsapp || 0) / serviceUsageStats.total_actions) * 100) : 0}% del uso
                        </p>
                      </div>
                      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/50 dark:bg-blue-900/20">
                        <p className="text-xs font-medium uppercase tracking-wide text-blue-700 dark:text-blue-300">Portal</p>
                        <p className="mt-1 text-3xl font-bold text-blue-700 dark:text-blue-300">{(serviceUsageStats.channel_totals?.portal || 0).toLocaleString()}</p>
                        <p className="text-xs text-blue-700/70 dark:text-blue-300/70">
                          {serviceUsageStats.total_actions > 0 ? Math.round(((serviceUsageStats.channel_totals?.portal || 0) / serviceUsageStats.total_actions) * 100) : 0}% del uso
                        </p>
                      </div>
                    </div>

                    <div className="w-full rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                      <ResponsiveContainer width="100%" height={340}>
                        <BarChart data={serviceUsageStats.usage} layout="vertical" margin={{ top: 12, right: 24, left: 28, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.3} />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis type="category" dataKey="service" width={135} tick={{ fontSize: 12 }} />
                          <Tooltip
                            cursor={{ fill: 'rgba(148, 163, 184, 0.10)' }}
                            formatter={(value, name) => [Number(value).toLocaleString(), name]}
                            contentStyle={{ fontSize: '12px', borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                          />
                          <Legend />
                          <Bar dataKey="whatsapp" name="WhatsApp" stackId="channel" fill="#10b981" radius={[4, 0, 0, 4]} />
                          <Bar dataKey="portal" name="Portal" stackId="channel" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-800/60">
                          <tr><th className="px-4 py-3 text-left">Funcionalidad</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right text-emerald-600">WhatsApp</th><th className="px-4 py-3 text-right text-blue-600">Portal</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                          {serviceUsageStats.usage.map((item) => (
                            <tr key={item.key}>
                              <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-200">{item.service}</td>
                              <td className="px-4 py-3 text-right font-bold">{(item.total ?? item.count ?? 0).toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-emerald-600 dark:text-emerald-400">{(item.whatsapp || 0).toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-blue-600 dark:text-blue-400">{(item.portal || 0).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-slate-500">Movimientos, viajes, suscripciones y tarjetas cuentan registros creados. Consultas generales cuenta preguntas financieras respondidas. Cada acción se atribuye al canal donde se realizó.</p>
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No hay datos disponibles</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            {/* User Detail Modal */}
            <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-['Outfit']">Detalle del Usuario</DialogTitle>
                </DialogHeader>
                {userDetailLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
                  </div>
                ) : selectedUserDetail ? (
                  <div className="space-y-4">
                    {/* User Info */}
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="font-bold text-lg">{selectedUserDetail.user?.phone}</p>
                      {selectedUserDetail.user?.email && (
                        <p className="text-sm text-slate-500">{selectedUserDetail.user.email}</p>
                      )}
                      {selectedUserDetail.user?.name && (
                        <p className="text-sm text-slate-600">{selectedUserDetail.user.name}</p>
                      )}
                      <div className="flex gap-2 mt-2">
                        <Badge variant={selectedUserDetail.user?.status === "active" ? "default" : "secondary"}>
                          {selectedUserDetail.user?.status === "active" ? "Activo" : "Inactivo"}
                        </Badge>
                        <Badge variant="outline">{selectedUserDetail.user?.currency || "COP"}</Badge>
                      </div>
                    </div>
                    
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 uppercase">Transacciones</p>
                        <p className="text-xl font-bold">{selectedUserDetail.stats?.total_transactions || 0}</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 uppercase">Deudas</p>
                        <p className="text-xl font-bold">{selectedUserDetail.stats?.total_debts || 0}</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 uppercase">Metas de Ahorro</p>
                        <p className="text-xl font-bold">{selectedUserDetail.stats?.total_savings_goals || 0}</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 uppercase">Suscripciones</p>
                        <p className="text-xl font-bold">{selectedUserDetail.stats?.total_subscriptions || 0}</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 uppercase">Tarjetas de Crédito</p>
                        <p className="text-xl font-bold">{selectedUserDetail.stats?.total_credit_cards || 0}</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-slate-500 uppercase">Recordatorios</p>
                        <p className="text-xl font-bold">{selectedUserDetail.stats?.total_reminders || 0}</p>
                      </div>
                    </div>
                    
                    {/* Dates */}
                    <div className="pt-3 border-t text-sm text-slate-500 space-y-1">
                      <p><strong>Registrado:</strong> {formatDate(selectedUserDetail.user?.created_at)}</p>
                      <p><strong>Vigencia del acceso:</strong> <AdminAccessValidity user={selectedUserDetail.user} formatDate={formatDate} /></p>
                      {selectedUserDetail.stats?.last_activity && (
                        <p><strong>Última actividad:</strong> {formatDate(selectedUserDetail.stats.last_activity)}</p>
                      )}
                    </div>
                  </div>
                ) : null}
              </DialogContent>
            </Dialog>

            {/* Users Table */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle className="font-['Outfit']">Gestión de Usuarios</CardTitle>
                  
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder="Buscar por teléfono, email o @usuario..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 w-64"
                        data-testid="search-users"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[130px]">
                        <SelectValue placeholder="Estado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="active">Activos</SelectItem>
                        <SelectItem value="inactive">Inactivos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                        <TableHead>Usuario</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Vencimiento</TableHead>
                        <TableHead>Registro</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedUsers.map((u) => (
                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{u.whatsapp_username || u.phone || "Sin identificador"}</p>
                              {u.whatsapp_username && u.phone && (
                                <p className="text-xs text-slate-500">{u.phone}</p>
                              )}
                              {u.email && (
                                <p className="text-xs text-slate-500">{u.email}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={u.status === "active" ? "default" : "secondary"}
                              className={u.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30" : ""}
                            >
                              {u.status === "active" ? "Activo" : "Inactivo"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <Badge variant="outline" className="w-fit">
                                {u.role === "admin" ? "Admin" : "Usuario"}
                              </Badge>
                              {["subscribed", "active"].includes(u.subscription_status) && (
                                <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 border-indigo-200 w-fit text-[10px]">
                                  {plans.find(p => p.id === u.plan_id)?.name || u.plan_name || "Suscripción"}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <AdminAccessValidity user={u} formatDate={formatDate} />
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDate(u.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* View User Detail Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => fetchUserDetail(u.id)}
                                className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 h-8 w-8 p-0"
                                data-testid={`view-user-${u.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              
                              {/* Backup Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => fetchUserBackups(u.id)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
                                data-testid={`backup-user-${u.id}`}
                                title="Ver backups"
                              >
                                <Database className="w-4 h-4" />
                              </Button>
                              
                              <Select
                                value={u.status}
                                onValueChange={(v) => handleUpdateUser(u.id, { status: v })}
                              >
                                <SelectTrigger className="w-[100px] h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active">Activar</SelectItem>
                                  <SelectItem value="inactive">Desactivar</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openSubscriptionModal(u)}
                                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-8 w-8 p-0"
                                title="Gestionar Suscripción"
                                data-testid={`manage-sub-${u.id}`}
                              >
                                <CreditCard className="w-4 h-4" />
                              </Button>
                              
                              {/* Delete User Button */}
                              {u.role !== "admin" && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 h-8 w-8 p-0"
                                      data-testid={`delete-user-${u.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Esta acción eliminará <strong>permanentemente</strong> al usuario <strong>{u.whatsapp_username || u.phone}</strong> y todos sus datos:
                                        <ul className="list-disc list-inside mt-2 text-sm">
                                          <li>Transacciones</li>
                                          <li>Deudas</li>
                                          <li>Suscripciones</li>
                                          <li>Metas de ahorro</li>
                                          <li>Recordatorios</li>
                                        </ul>
                                        <p className="mt-2 text-rose-600 font-medium">
                                          Si el usuario vuelve a escribir al bot, será registrado como usuario nuevo.
                                        </p>
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDeleteUser(u.id, u.whatsapp_username || u.phone)}
                                        className="bg-rose-600 hover:bg-rose-700 text-white"
                                      >
                                        Eliminar Usuario
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No se encontraron usuarios
                  </div>
                )}
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <p className="text-sm text-slate-500">
                      Mostrando {((currentPage - 1) * USERS_PER_PAGE) + 1} - {Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)} de {filteredUsers.length}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        data-testid="prev-page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm px-2">
                        Página {currentPage} de {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        data-testid="next-page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Plans Tab - Mercado Pago Billing */}
          <TabsContent value="plans" className="space-y-6">
            {/* Billing Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="chart-container">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-100 dark:bg-cyan-900 rounded-lg">
                      <Package className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Planes Activos</p>
                      <p className="text-2xl font-bold text-cyan-600">{billingStats?.plans?.active || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="chart-container">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 dark:bg-emerald-900 rounded-lg">
                      <Users className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Suscripciones Activas</p>
                      <p className="text-2xl font-bold text-emerald-600">{billingStats?.subscriptions?.active || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="chart-container">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                      <CreditCard className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Intentos de Pago (30d)</p>
                      <p className="text-2xl font-bold text-amber-600">{billingStats?.recent_payment_attempts || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Plans Management */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-6 h-6 text-cyan-600" />
                    <div>
                      <CardTitle className="font-['Outfit']">Planes de Suscripción</CardTitle>
                      <p className="text-xs text-slate-500 mt-1">Gestiona los planes de pago de Aorra</p>
                    </div>
                  </div>
                  <Button onClick={() => handleOpenPlanDialog()} className="gap-2" data-testid="btn-new-plan">
                    <Plus className="w-4 h-4" />
                    Nuevo Plan
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {plansLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
                  </div>
                ) : plans.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay planes creados</p>
                    <p className="text-sm">Crea tu primer plan de suscripción</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {plans.map((plan) => (
                      <div
                        key={plan.id}
                        className={`p-4 border rounded-lg ${plan.is_active ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-900 opacity-60'}`}
                        data-testid={`plan-${plan.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-bold text-lg">{plan.name}</h3>
                              {!plan.is_active && (
                                <Badge variant="outline" className="text-xs">Inactivo</Badge>
                              )}
                              {plan.is_featured && (
                                <Badge className="text-xs bg-amber-500 text-white gap-1">⭐ Más Vendido</Badge>
                              )}
                              <Badge variant="secondary" className="text-xs">
                                {plan.billing_period === 'yearly' ? 'Anual' : plan.billing_period === 'quarterly' ? 'Trimestral' : 'Mensual'}
                              </Badge>
                              <Badge 
                                variant={plan.payment_type === 'recurring' ? 'default' : 'outline'} 
                                className={`text-xs ${plan.payment_type === 'recurring' ? 'bg-emerald-500' : ''}`}
                              >
                                {plan.payment_type === 'recurring' ? '🔄 Recurrente' : '💳 Pago Único'}
                              </Badge>
                            </div>
                            <p className="text-2xl font-bold text-cyan-600">
                              ${plan.price?.toLocaleString('es-CO')} <span className="text-sm text-slate-500">{plan.currency}</span>
                            </p>
                            {plan.description && (
                              <p className="text-sm text-slate-500 mt-1">{plan.description}</p>
                            )}
                            {plan.features?.length > 0 && (
                              <ul className="mt-3 space-y-1">
                                {plan.features.map((feature, idx) => (
                                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                                    <Check className="w-4 h-4 text-emerald-500" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {plan.trial_days > 0 && (
                              <p className="text-xs text-slate-500 mt-2">
                                Incluye {plan.trial_days} días de prueba
                              </p>
                            )}
                            {plan.button_text && (
                              <p className="text-xs text-cyan-600 mt-1 font-medium">
                                Botón: "{plan.button_text}"
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleDuplicatePlan(plan)} title="Duplicar plan" data-testid={`duplicate-plan-${plan.id}`}>
                              <Copy className="w-4 h-4 text-cyan-600" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleOpenPlanDialog(plan)} data-testid={`edit-plan-${plan.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" data-testid={`delete-plan-${plan.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>¿Eliminar plan?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    El plan "{plan.name}" será desactivado. Los usuarios con este plan mantendrán su suscripción actual.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeletePlan(plan.id)} className="bg-red-500 hover:bg-red-600">
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Plan Dialog */}
            <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
              <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                  <DialogTitle className="font-['Outfit']">
                    {planEditId ? 'Editar Plan' : 'Nuevo Plan'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 overflow-y-auto flex-1 pr-2">
                  <div>
                    <Label>Nombre del Plan *</Label>
                    <Input
                      value={planForm.name}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ej: Plan Básico"
                      data-testid="plan-name-input"
                    />
                  </div>
                  <div>
                    <Label>Descripción</Label>
                    <Input
                      value={planForm.description}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Descripción del plan"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Precio *</Label>
                      <Input
                        type="number"
                        value={planForm.price}
                        onChange={(e) => setPlanForm(prev => ({ ...prev, price: e.target.value }))}
                        placeholder="29900"
                        data-testid="plan-price-input"
                      />
                    </div>
                    <div>
                      <Label>Moneda</Label>
                      <Select value={planForm.currency} onValueChange={(v) => setPlanForm(prev => ({ ...prev, currency: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="COP">COP</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="MXN">MXN</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Período de Facturación</Label>
                      <Select value={planForm.billing_period} onValueChange={(v) => setPlanForm(prev => ({ ...prev, billing_period: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Mensual</SelectItem>
                          <SelectItem value="quarterly">Trimestral</SelectItem>
                          <SelectItem value="yearly">Anual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tipo de Pago</Label>
                      <Select value={planForm.payment_type} onValueChange={(v) => setPlanForm(prev => ({ ...prev, payment_type: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="one_time">Pago Único</SelectItem>
                          <SelectItem value="recurring">Recurrente (Auto-débito)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="mt-1 text-xs text-slate-500">Mercado Pago cobra todos los planes como suscripciones cada 1, 3 o 12 meses, según su vigencia. Esta selección conserva la clasificación de los pagos anteriores.</p>
                    </div>
                  </div>
                  <div>
                    <Label>Días de Prueba</Label>
                    <Input
                      type="number"
                      value={planForm.trial_days}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 0 }))}
                      placeholder="14"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <WorkspacePlanFields value={planForm.workspace_capabilities}
                      onChange={value => setPlanForm(prev => ({ ...prev, workspace_capabilities: value }))} />
                    <Label>Características</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        placeholder="Nueva característica"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                      />
                      <Button type="button" variant="outline" onClick={handleAddFeature}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {planForm.features.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {planForm.features.map((feat, idx) => (
                          <li key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded text-sm">
                            <span className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-emerald-500" />
                              {feat}
                            </span>
                            <button onClick={() => handleRemoveFeature(idx)} className="text-red-500 hover:text-red-600">
                              <XCircle className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <Label>Texto del Botón CTA</Label>
                    <Input
                      value={planForm.button_text}
                      onChange={(e) => setPlanForm(prev => ({ ...prev, button_text: e.target.value }))}
                      placeholder="Ej: Comenzar Prueba Gratuita"
                    />
                    <p className="text-xs text-slate-500 mt-1">Texto que aparece en el botón de la landing. Si está vacío usa el texto por defecto.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={planForm.is_featured}
                      onCheckedChange={(v) => setPlanForm(prev => ({ ...prev, is_featured: v }))}
                    />
                    <Label>⭐ Plan Más Vendido (destacado en la landing)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={planForm.is_active}
                      onCheckedChange={(v) => setPlanForm(prev => ({ ...prev, is_active: v }))}
                    />
                    <Label>Plan Activo</Label>
                  </div>
                </div>
                <DialogFooter className="mt-4 flex-shrink-0 border-t pt-4">
                  <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={handleSavePlan} disabled={planSubmitting} data-testid="save-plan-btn">
                    {planSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {planEditId ? 'Actualizar' : 'Crear'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Card>
              <CardHeader><CardTitle>Planes vendidos en Shopify</CardTitle><CardDescription>Asocia cada SKU con los servicios de un plan. Las compras ya activadas conservan sus condiciones.</CardDescription></CardHeader>
              <CardContent className="space-y-4">
                <Button variant="outline" onClick={() => { setSkuForm({ sku: '', months: '', description: '', plan_id: '' }); setSkuDialogOpen(true); }}>Configurar SKU</Button>
                {skuConfigs.map(config => <div key={config.sku} className="flex flex-wrap items-center justify-between gap-3 rounded border p-3">
                  <div><p className="font-medium">{config.sku}</p><p className="text-sm text-slate-500">{config.months} mes(es) · {config.plan_id ? plans.find(plan => plan.id === config.plan_id)?.name || 'Plan no disponible' : 'Servicios Base'}</p></div>
                  <div className="flex gap-2"><Button variant="outline" onClick={() => { setSkuForm({ ...config, plan_id: config.plan_id || '' }); setSkuDialogOpen(true); }}>Editar</Button>
                    <Button variant="ghost" onClick={() => handleDeleteSku(config.sku)}>Eliminar</Button></div>
                </div>)}
              </CardContent>
            </Card>
            <Dialog open={skuDialogOpen} onOpenChange={setSkuDialogOpen}>
              <DialogContent><DialogHeader><DialogTitle>Configurar producto de Shopify</DialogTitle><DialogDescription>Los límites se copian del plan al activar una compra confirmada.</DialogDescription></DialogHeader>
                <form onSubmit={handleAddSku} className="space-y-4">
                  <label className="block text-sm">SKU<Input required maxLength={100} value={skuForm.sku} onChange={e => setSkuForm({ ...skuForm, sku: e.target.value })} /></label>
                  <label className="block text-sm">Meses de acceso<Input type="number" min={1} max={120} required value={skuForm.months} onChange={e => setSkuForm({ ...skuForm, months: e.target.value })} /></label>
                  <label className="block text-sm">Plan incluido<select className="mt-1 w-full rounded border p-2" value={skuForm.plan_id} onChange={e => setSkuForm({ ...skuForm, plan_id: e.target.value })}>
                    <option value="">Servicios Base (un tablero personal)</option>
                    {plans.filter(plan => plan.is_active || plan.id === skuForm.plan_id).map(plan => <option key={plan.id} value={plan.id} disabled={!plan.is_active}>{plan.name}{!plan.is_active ? ' (inactivo)' : ''}</option>)}
                  </select></label>
                  <label className="block text-sm">Descripción<Input value={skuForm.description} onChange={e => setSkuForm({ ...skuForm, description: e.target.value })} /></label>
                  <Button type="submit" disabled={skuSubmitting}>{skuSubmitting ? 'Guardando…' : 'Guardar configuración'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <AdminWeeklySummarySettings api={api} />
            <Card className="chart-container border-2 border-emerald-500/20 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-50 to-cyan-50 dark:from-emerald-950/30 dark:to-cyan-950/30">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-emerald-600 text-white"><Flame className="w-5 h-5" /></div>
                    <div>
                      <CardTitle className="font-['Outfit']">Reactivación por inactividad</CardTitle>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">
                        Solo para suscriptores activos. Un gasto nuevo o cualquier mensaje al bot reinicia la secuencia.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl bg-white/80 dark:bg-slate-900/70 border px-4 py-3">
                    <div>
                      <p className="font-medium text-sm">Automatización activa</p>
                      <p className="text-xs text-slate-500">Se evalúa cada 5 minutos</p>
                    </div>
                    <Switch
                      checked={Boolean(inactivitySettings.enabled)}
                      onCheckedChange={(checked) => setInactivitySettings((current) => ({ ...current, enabled: checked }))}
                      data-testid="inactivity-enabled"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl border bg-slate-50 dark:bg-slate-900 p-4">
                    <p className="text-xs text-slate-500">Aceptadas por Meta</p>
                    <p className="text-2xl font-bold mt-1">{inactivitySettings.stats?.accepted || 0}</p>
                  </div>
                  <div className="rounded-xl border bg-emerald-50/60 dark:bg-emerald-950/20 p-4">
                    <p className="text-xs text-emerald-700 dark:text-emerald-400">Entregadas</p>
                    <p className="text-2xl font-bold mt-1 text-emerald-700 dark:text-emerald-400">{inactivitySettings.stats?.delivered || 0}</p>
                  </div>
                  <div className="rounded-xl border bg-rose-50/60 dark:bg-rose-950/20 p-4">
                    <p className="text-xs text-rose-700 dark:text-rose-400">Fallidas</p>
                    <p className="text-2xl font-bold mt-1 text-rose-700 dark:text-rose-400">{inactivitySettings.stats?.failed || 0}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-semibold">Secuencia de mensajes</h3>
                      <p className="text-xs text-slate-500">Las horas de cada etapa se cuentan desde la actividad o plantilla anterior.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={addInactivityStage} disabled={(inactivitySettings.stages || []).length >= 10}>
                      <Plus className="w-4 h-4 mr-2" /> Añadir etapa
                    </Button>
                  </div>

                  {(inactivitySettings.stages || []).map((stage, index) => {
                    const cumulativeHours = inactivitySettings.stages
                      .slice(0, index + 1)
                      .filter((item) => item.enabled !== false)
                      .reduce((total, item) => total + (Number(item.delay_hours) || 0), 0);
                    return (
                      <div key={stage.id || index} className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-bold">{index + 1}</span>
                            <div>
                              <p className="font-semibold">{stage.label || `Recordatorio ${index + 1}`}</p>
                              <p className="text-xs text-slate-500">Aproximadamente a las {cumulativeHours} horas de inactividad</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {stage.meta_submission && (
                              <Badge variant="outline" className="text-emerald-700 border-emerald-300">
                                Meta: {stage.meta_submission.status}
                              </Badge>
                            )}
                            <Switch
                              checked={stage.enabled !== false}
                              onCheckedChange={(checked) => updateInactivityStage(index, "enabled", checked)}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeInactivityStage(index)}
                              disabled={(inactivitySettings.stages || []).length <= 1}
                              aria-label={`Eliminar etapa ${index + 1}`}
                            >
                              <Trash2 className="w-4 h-4 text-rose-600" />
                            </Button>
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="space-y-2">
                            <Label>Nombre de la etapa</Label>
                            <Input value={stage.label || ""} onChange={(event) => updateInactivityStage(index, "label", event.target.value)} />
                          </div>
                          <div className="space-y-2">
                            <Label>Horas de espera</Label>
                            <Input
                              type="number"
                              min="1"
                              max="8760"
                              value={stage.delay_hours}
                              onChange={(event) => updateInactivityStage(index, "delay_hours", event.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Plantilla de Meta</Label>
                            <Input
                              value={stage.template_name || ""}
                              placeholder="recordatorio_inactividad_48h"
                              onChange={(event) => updateInactivityStage(index, "template_name", event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Contenido de la plantilla</Label>
                          <textarea
                            className="w-full min-h-[92px] p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                            value={stage.body_text || ""}
                            maxLength={1024}
                            onChange={(event) => updateInactivityStage(index, "body_text", event.target.value)}
                          />
                          <p className="text-xs text-slate-500">
                            Incluye exactamente <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{{1}}"}</code>; ahí se insertará el gasto sugerido para cada usuario.
                          </p>
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                            <div className="space-y-2 w-full sm:max-w-xs">
                              <Label>Texto del botón</Label>
                              <Input
                                value={stage.button_text || ""}
                                maxLength={25}
                                onChange={(event) => updateInactivityStage(index, "button_text", event.target.value)}
                              />
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => handleCreateInactivityTemplate(stage.id)}
                              disabled={templateSubmitting === stage.id || inactivitySubmitting}
                            >
                              {templateSubmitting === stage.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                              Crear en Meta
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 p-4 text-sm text-blue-900 dark:text-blue-200">
                  <strong>Personalización y protecciones:</strong> se usa primero el gasto concreto más repetido; si no existe, el último gasto claramente no esencial. Nunca se muestran pagos de deuda, tarjeta, ahorros o transferencias. Además, no se envía a cuentas inactivas ni suscripciones vencidas o canceladas.
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={() => handleSaveInactivitySettings()} disabled={inactivitySubmitting || !(inactivitySettings.stages || []).length} className="flex-1">
                    {inactivitySubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar secuencia
                  </Button>
                  <Button variant="outline" onClick={handleRunInactivityNotifications} disabled={inactivityRunning || !inactivitySettings.enabled}>
                    {inactivityRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                    Ejecutar revisión ahora
                  </Button>
                </div>

                {(inactivitySettings.recent_deliveries || []).length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold">Entregas recientes</h3>
                    <div className="rounded-xl border overflow-hidden">
                      <Table>
                        <TableHeader><TableRow><TableHead>Usuario</TableHead><TableHead>Etapa</TableHead><TableHead>Sugerencia</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {inactivitySettings.recent_deliveries.slice(0, 8).map((delivery, index) => (
                            <TableRow key={`${delivery.phone}-${delivery.attempted_at}-${index}`}>
                              <TableCell className="font-mono text-xs">{delivery.phone}</TableCell>
                              <TableCell>{delivery.stage_label || delivery.template}</TableCell>
                              <TableCell>{delivery.expense_suggestion || "—"}</TableCell>
                              <TableCell><Badge variant="outline">{delivery.status}</Badge></TableCell>
                              <TableCell className="text-xs text-slate-500">{delivery.attempted_at ? new Date(delivery.attempted_at).toLocaleString() : "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <AdminSubscriptionReminderSettings api={api} />

            {/* Payment Notification Settings */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-cyan-600" />
                  <div>
                    <CardTitle className="font-['Outfit']">Notificaciones de Pagos</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      Recordatorios automáticos para suscripciones, tarjetas y deudas (12 horas antes)
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300">Notificaciones de Pagos Activas</p>
                    <p className="text-xs text-slate-500">Enviar recordatorios de pagos próximos</p>
                  </div>
                  <Switch
                    checked={paymentNotifSettings.enabled}
                    onCheckedChange={(checked) => setPaymentNotifSettings({...paymentNotifSettings, enabled: checked})}
                  />
                </div>

                {/* Template Name */}
                <div className="space-y-2">
                  <Label htmlFor="payment-template">Nombre de Plantilla de Meta</Label>
                  <Input
                    id="payment-template"
                    placeholder="Ej: payment_reminder"
                    value={paymentNotifSettings.template_name}
                    onChange={(e) => setPaymentNotifSettings({...paymentNotifSettings, template_name: e.target.value})}
                    disabled={!paymentNotifSettings.enabled}
                  />
                  <p className="text-xs text-slate-500">
                    La plantilla debe tener estas variables: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">nombre</code> y <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">fecha</code>
                  </p>
                </div>

                {/* Template Preview */}
                <div className="space-y-2">
                  <Label>Texto de la Notificación (Vista Previa)</Label>
                  <textarea
                    className="w-full min-h-[80px] p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                    value={paymentNotifSettings.template_text}
                    onChange={(e) => setPaymentNotifSettings({...paymentNotifSettings, template_text: e.target.value})}
                    disabled={!paymentNotifSettings.enabled}
                    placeholder="Hola, solo paso a recordarte que tu {tipo_pago} de {nombre} vence el {fecha_vencimiento}."
                  />
                  <p className="text-xs text-slate-500">
                    Variables disponibles: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{nombre}"}</code>, <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{fecha_vencimiento}"}</code>, <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{tipo_pago}"}</code>
                  </p>
                </div>

                {/* Example Preview */}
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <p className="text-xs font-bold text-emerald-700 dark:text-emerald-300 mb-2">Ejemplo de mensaje:</p>
                  <p className="text-sm text-emerald-800 dark:text-emerald-200">
                    "Hola, solo paso a recordarte que tu <strong>suscripción</strong> de <strong>Netflix</strong> vence el <strong>15 de enero</strong>."
                  </p>
                </div>

                <Button
                  onClick={handleSavePaymentNotifSettings}
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  disabled={paymentNotifSubmitting}
                >
                  {paymentNotifSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar Configuración
                </Button>
              </CardContent>
            </Card>

            {/* Event Reminder Templates */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Calendar className="w-6 h-6 text-purple-600" />
                  <div>
                    <CardTitle className="font-['Outfit']">Plantillas de Recordatorios de Eventos</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      Configura las plantillas de Meta para notificaciones de eventos y citas
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Plantilla para Recordatorio al Usuario</Label>
                  <Input
                    placeholder="Ej: evento_recordatorio"
                    value={eventTemplates.owner_template}
                    onChange={(e) => setEventTemplates({...eventTemplates, owner_template: e.target.value})}
                  />
                  <p className="text-xs text-slate-500">
                    Se usa para recordar al usuario sobre su evento. Variable: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{1}"}</code> = título del evento
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Plantilla para Invitación a Invitados</Label>
                  <Input
                    placeholder="Ej: evento_invitacion"
                    value={eventTemplates.invitee_template}
                    onChange={(e) => setEventTemplates({...eventTemplates, invitee_template: e.target.value})}
                  />
                  <p className="text-xs text-slate-500">
                    Se usa para notificar a los invitados. Variables: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{1}"}</code> = nombre del anfitrión, <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{2}"}</code> = título
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Plantilla para Confirmación de Asistencia</Label>
                  <Input
                    placeholder="Ej: evento_confirmacion"
                    value={eventTemplates.confirmation_template}
                    onChange={(e) => setEventTemplates({...eventTemplates, confirmation_template: e.target.value})}
                  />
                  <p className="text-xs text-slate-500">
                    Se usa para informar al usuario cuando un invitado confirma/rechaza. Variable: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{1}"}</code> = nombre del invitado
                  </p>
                </div>

                <Button
                  onClick={handleSaveEventTemplates}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={eventTemplatesSubmitting}
                >
                  {eventTemplatesSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar Plantillas de Eventos
                </Button>
              </CardContent>
            </Card>

            {/* Habit Reminder Templates */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-orange-600" />
                  <div>
                    <CardTitle className="font-['Outfit']">Plantillas de Recordatorios de Hábitos</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                      Configura las plantillas de Meta para recordatorios de hábitos y rutinas
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Plantilla para Hábito Individual (1 pendiente)</Label>
                  <Input
                    placeholder="Ej: habito_individual"
                    value={habitTemplates.single_template}
                    onChange={(e) => setHabitTemplates({...habitTemplates, single_template: e.target.value})}
                  />
                  <p className="text-xs text-slate-500">
                    Se usa cuando hay UN solo hábito pendiente. Variable: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{1}"}</code> = nombre del hábito
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Plantilla para Múltiples Hábitos (2+ pendientes)</Label>
                  <Input
                    placeholder="Ej: habitos_multiples"
                    value={habitTemplates.multiple_template}
                    onChange={(e) => setHabitTemplates({...habitTemplates, multiple_template: e.target.value})}
                  />
                  <p className="text-xs text-slate-500">
                    Se usa cuando hay VARIOS hábitos pendientes. Variables: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{1}"}</code> = cantidad, <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{2}"}</code> = lista de hábitos
                  </p>
                </div>

                <Button
                  onClick={handleSaveHabitTemplates}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  disabled={habitTemplatesSubmitting}
                >
                  {habitTemplatesSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar Plantillas de Hábitos
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Streaks Tab */}
          <TabsContent value="streaks" className="space-y-6">
            <Card className="chart-container border-2 border-orange-200 dark:border-orange-900/50">
              <CardHeader>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <Flame className="h-7 w-7 text-orange-500" />
                    <div>
                      <CardTitle className="font-['Outfit']">Rachas financieras</CardTitle>
                      <p className="mt-1 text-sm text-slate-500">
                        El apagado global congela los días afectados y oculta la experiencia al usuario.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl bg-orange-50 px-4 py-3 dark:bg-orange-950/30">
                    <Label htmlFor="global-streaks">Sistema activo</Label>
                    <Switch
                      id="global-streaks"
                      checked={Boolean(streakSettings?.enabled)}
                      onCheckedChange={handleStreakGlobalToggle}
                      disabled={
                        !streakSettings?.activation_ready ||
                        (!streakSettings?.enabled && streakReadiness?.ready_for_activation === false)
                      }
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!streakSettings?.activation_ready && (
                  <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                    El runtime todavía no reporta preparación completa; la activación permanece bloqueada.
                  </p>
                )}
                {streakSettings?.launched_at && (
                  <p className="text-xs text-slate-500">
                    Inicio sin reconstrucción histórica: {new Date(streakSettings.launched_at).toLocaleString("es-CO")}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="chart-container">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="font-['Outfit'] flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-emerald-600" /> Preparación de lanzamiento
                    </CardTitle>
                    <p className="mt-1 text-sm text-slate-500">
                      Revisión dinámica de infraestructura, reparaciones, plantillas y entregas.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      streakReadiness?.status === "ready"
                        ? "bg-emerald-100 text-emerald-800"
                        : streakReadiness?.status === "blocked"
                          ? "bg-rose-100 text-rose-800"
                          : "bg-amber-100 text-amber-800"
                    }>
                      {streakReadiness?.status === "ready" ? "Listo" : streakReadiness?.status === "blocked" ? "Bloqueado" : "Requiere atención"}
                    </Badge>
                    <Button variant="outline" size="sm" onClick={fetchStreakAdmin}>
                      <RefreshCw className="mr-2 h-4 w-4" /> Actualizar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {(streakReadiness?.checks || []).map((check) => (
                    <div key={check.id} className="flex items-start gap-3 rounded-xl border p-3">
                      {check.status === "pass" ? (
                        <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                      ) : check.status === "blocker" ? (
                        <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
                      ) : (
                        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                      )}
                      <div>
                        <p className="text-sm font-bold">{check.label}</p>
                        <p className="mt-1 text-xs text-slate-500">{check.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {streakReadiness?.last_notification_run && (
                  <p className="text-xs text-slate-500">
                    Última revisión automática: {new Date(streakReadiness.last_notification_run.finished_at).toLocaleString("es-CO")}
                    {` · ${streakReadiness.last_notification_run.duration_ms || 0} ms`}
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              {[
                ["Perfiles", streakReport.overview?.profiles || 0],
                ["Elegibles", streakReport.overview?.eligible || 0],
                ["Protegidos hoy", streakReport.overview?.protected || 0],
                ["En riesgo", streakReport.overview?.at_risk || 0],
                ["Mayor racha", streakReport.overview?.max_current_streak || 0]
              ].map(([label, value]) => (
                <Card key={label} className="stat-card">
                  <CardContent className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                    <p className="mt-2 text-3xl font-bold">{value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-orange-500" /> Ranking y búsqueda
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 md:grid-cols-4">
                  <Input
                    placeholder="Nombre, correo o teléfono"
                    value={streakFilters.search}
                    onChange={(event) => setStreakFilters({ ...streakFilters, search: event.target.value })}
                  />
                  <Input
                    type="number"
                    min="0"
                    placeholder="Días actuales exactos"
                    value={streakFilters.exact_days}
                    onChange={(event) => setStreakFilters({ ...streakFilters, exact_days: event.target.value })}
                  />
                  <Select
                    value={streakFilters.status || "all"}
                    onValueChange={(value) => setStreakFilters({ ...streakFilters, status: value === "all" ? "" : value })}
                  >
                    <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      <SelectItem value="protected">Protegido</SelectItem>
                      <SelectItem value="at_risk">En riesgo</SelectItem>
                      <SelectItem value="not_started">Sin iniciar</SelectItem>
                      <SelectItem value="user_disabled">Desactivado por usuario</SelectItem>
                      <SelectItem value="subscription_inactive">Suscripción inactiva</SelectItem>
                      <SelectItem value="timezone_required">Falta zona horaria</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={streakFilters.sort_by}
                    onValueChange={(value) => setStreakFilters({ ...streakFilters, sort_by: value })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="current_streak">Racha actual</SelectItem>
                      <SelectItem value="longest_streak">Récord histórico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => fetchStreakReport({ page: 1 })} disabled={streakLoading}>
                    {streakLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                    Consultar
                  </Button>
                </div>

                <div className="overflow-x-auto rounded-xl border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Actual</TableHead>
                        <TableHead>Récord</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Último día</TableHead>
                        <TableHead className="text-right">Detalle</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(streakReport.items || []).map((item) => (
                        <TableRow key={item.user_id}>
                          <TableCell>
                            <p className="font-semibold">{item.user?.name || "Sin nombre"}</p>
                            <p className="text-xs text-slate-500">{item.user?.email || item.user?.phone || item.user_id}</p>
                          </TableCell>
                          <TableCell className="font-bold text-orange-600">{item.current_streak || 0}</TableCell>
                          <TableCell>{item.longest_streak || 0}</TableCell>
                          <TableCell><Badge variant="outline">{item.status || "not_started"}</Badge></TableCell>
                          <TableCell>{item.last_qualified_date || "—"}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" onClick={() => handleOpenStreakDetail(item.user_id)}>
                              <Eye className="mr-1 h-4 w-4" /> Ver
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!streakLoading && !(streakReport.items || []).length && (
                        <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-500">No hay resultados.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">{streakReport.total || 0} usuarios</p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={streakLoading || streakReport.page <= 1}
                      onClick={() => fetchStreakReport({ page: streakReport.page - 1 })}
                    ><ChevronLeft className="h-4 w-4" /></Button>
                    <span className="text-sm">Página {streakReport.page || 1} de {Math.max(1, streakReport.pages || 1)}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={streakLoading || streakReport.page >= streakReport.pages}
                      onClick={() => fetchStreakReport({ page: streakReport.page + 1 })}
                    ><ChevronRight className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="chart-container">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="font-['Outfit'] flex items-center gap-2"><Bell className="h-5 w-5" /> Recordatorios de racha</CardTitle>
                    <p className="mt-1 text-sm text-slate-500">Los envíos proactivos usan plantillas aprobadas de Meta y se deduplican por usuario, etapa y fecha límite.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="streak-notifications-enabled">Envíos activos</Label>
                    <Switch
                      id="streak-notifications-enabled"
                      checked={Boolean(streakNotifications.enabled)}
                      onCheckedChange={(checked) => setStreakNotifications({ ...streakNotifications, enabled: checked })}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="rounded-lg border p-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <Switch id="streak-free-reminders"
                      checked={Boolean(streakNotifications.free_users_enabled)}
                      onCheckedChange={checked => setStreakNotifications({ ...streakNotifications, free_users_enabled: checked })} />
                    <Label htmlFor="streak-free-reminders">Recordatorios para usuarios gratuitos</Label>
                  </div>
                  <p className="text-sm text-slate-500">Requiere que las rachas y los envíos estén activos. Al activarlo por primera vez comienzan las rachas gratuitas, sin premios. Después puedes apagar los avisos y conservar su progreso. Los usuarios con pago vigente mantienen sus recordatorios.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-4">
                  {[
                    ["Aceptados", streakNotifications.stats?.accepted || 0],
                    ["Entregados", streakNotifications.stats?.delivered || 0],
                    ["Fallidos", streakNotifications.stats?.failed || 0],
                    ["Cancelados", streakNotifications.stats?.cancelled || 0]
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border p-3">
                      <p className="text-xs text-slate-500">{label}</p><p className="text-2xl font-bold">{value}</p>
                    </div>
                  ))}
                </div>

                {(streakNotifications.stages || []).map((stage, index) => (
                  <div key={stage.id} className="space-y-3 rounded-xl border p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Switch checked={stage.enabled !== false} onCheckedChange={(value) => updateStreakStage(index, "enabled", value)} />
                        <Input value={stage.label} onChange={(event) => updateStreakStage(index, "label", event.target.value)} />
                        {stage.meta_submission?.status && (
                          <Badge variant="outline">
                            Meta: {stage.meta_submission.status}
                            {stage.meta_submission.definition_current === false ? " · definición anterior" : ""}
                          </Badge>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" disabled={(streakNotifications.stages || []).length <= 1} onClick={() => removeStreakStage(index)}>
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                    <div className="grid gap-3 md:grid-cols-4">
                      <Select value={stage.timing_type} onValueChange={(value) => updateStreakStage(index, "timing_type", value)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hours_before_loss">Horas antes de perderla</SelectItem>
                          <SelectItem value="hours_after_last_activity">Horas después de última actividad</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" min="1" max="47" value={stage.hours} onChange={(event) => updateStreakStage(index, "hours", event.target.value)} />
                      <Input value={stage.template_name} onChange={(event) => updateStreakStage(index, "template_name", event.target.value)} placeholder="nombre_plantilla_meta" />
                      <Input value={stage.button_text} onChange={(event) => updateStreakStage(index, "button_text", event.target.value)} placeholder="Texto del botón" />
                    </div>
                    <textarea
                      className="min-h-[90px] w-full rounded-md border bg-background px-3 py-2 text-sm"
                      value={stage.body_text}
                      onChange={(event) => updateStreakStage(index, "body_text", event.target.value)}
                    />
                    <p className="text-xs text-slate-500">Variables obligatorias: {"{{1}}"} = días de racha; {"{{2}}"} = horas restantes calculadas al enviar.</p>
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={() => handleSubmitStreakTemplate(stage.id)} disabled={streakTemplateSubmitting === stage.id}>
                        {streakTemplateSubmitting === stage.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                        {stage.meta_submission?.status ? "Consultar estado en Meta" : "Enviar a aprobación de Meta"}
                      </Button>
                    </div>
                  </div>
                ))}

                <Button variant="outline" onClick={addStreakStage} disabled={(streakNotifications.stages || []).length >= 10}>
                  <Plus className="mr-2 h-4 w-4" /> Agregar recordatorio
                </Button>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button className="flex-1" onClick={handleSaveStreakNotifications} disabled={streakNotificationSaving}>
                    {streakNotificationSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Guardar configuración
                  </Button>
                  <Button variant="outline" onClick={handlePreviewStreakNotifications} disabled={streakPreviewLoading}>
                    {streakPreviewLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Simular revisión
                  </Button>
                  <Button variant="outline" onClick={handleRunStreakNotifications} disabled={streakNotificationRunning || !streakNotifications.enabled}>
                    {streakNotificationRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Ejecutar revisión
                  </Button>
                </div>
                {streakPreview && (
                  <div className="space-y-3 rounded-xl border border-sky-200 bg-sky-50/60 p-4">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <p className="font-bold text-sky-900">Simulación sin envíos</p>
                      <p className="text-xs text-sky-700">
                        {streakPreview.generated_at ? new Date(streakPreview.generated_at).toLocaleString("es-CO") : ""}
                      </p>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-4">
                      {[
                        ["Elegibles", streakPreview.eligible || 0],
                        ["Recordatorios vencidos", streakPreview.due || 0],
                        ["Se enviarían", streakPreview.would_send || 0],
                        ["Sin destino", streakPreview.missing_target || 0]
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-lg bg-white p-3">
                          <p className="text-xs text-slate-500">{label}</p>
                          <p className="text-xl font-bold">{value}</p>
                        </div>
                      ))}
                    </div>
                    {(streakPreview.sample || []).length > 0 && (
                      <div className="overflow-x-auto rounded-xl border bg-white">
                        <Table>
                          <TableHeader><TableRow><TableHead>Usuario</TableHead><TableHead>Etapa</TableHead><TableHead>Racha</TableHead><TableHead>Horas</TableHead><TableHead>Destino</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {streakPreview.sample.map((item, index) => (
                              <TableRow key={`${item.user_id}-${item.stage_id}-${index}`}>
                                <TableCell>{item.user_id}</TableCell>
                                <TableCell>{item.stage_label}</TableCell>
                                <TableCell>{item.current_streak}</TableCell>
                                <TableCell>{item.remaining_hours}</TableCell>
                                <TableCell>{item.has_target ? "Disponible" : "Faltante"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
                {(streakNotifications.recent_deliveries || []).length > 0 && (
                  <div>
                    <h4 className="mb-2 font-bold">Entregas recientes</h4>
                    <div className="overflow-x-auto rounded-xl border">
                      <Table>
                        <TableHeader><TableRow><TableHead>Usuario</TableHead><TableHead>Etapa</TableHead><TableHead>Horas restantes</TableHead><TableHead>Estado</TableHead><TableHead>Intento</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {streakNotifications.recent_deliveries.map((delivery, index) => (
                            <TableRow key={`${delivery.user_id}-${delivery.attempted_at}-${index}`}>
                              <TableCell>{delivery.user_id}</TableCell>
                              <TableCell>{delivery.stage_label}</TableCell>
                              <TableCell>{delivery.remaining_hours ?? "—"}</TableCell>
                              <TableCell><Badge variant="outline">{delivery.status}</Badge></TableCell>
                              <TableCell>{delivery.attempted_at ? new Date(delivery.attempted_at).toLocaleString("es-CO") : "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Dialog open={streakDetailOpen} onOpenChange={setStreakDetailOpen}>
              <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Detalle de racha — {streakDetail?.user?.name || "Usuario"}</DialogTitle>
                  <DialogDescription>Evidencia, días proyectados, runs históricos y movimientos asociados.</DialogDescription>
                </DialogHeader>
                {!streakDetail ? (
                  <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin" /></div>
                ) : (
                  <div className="space-y-5">
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-xl border p-3"><p className="text-xs text-slate-500">Racha actual</p><p className="text-2xl font-bold">{streakDetail.profile?.current_streak || 0}</p></div>
                      <div className="rounded-xl border p-3"><p className="text-xs text-slate-500">Récord</p><p className="text-2xl font-bold">{streakDetail.profile?.longest_streak || 0}</p></div>
                      <div className="rounded-xl border p-3"><p className="text-xs text-slate-500">Zona horaria</p><p className="font-semibold">{streakDetail.profile?.timezone || "Pendiente"}</p></div>
                    </div>
                    <div>
                      <h4 className="mb-2 font-bold">Runs históricos</h4>
                      <div className="flex flex-wrap gap-2">
                        {(streakDetail.runs || []).map((run) => <Badge key={`${run.started_on}-${run.ended_on}`} variant="outline">{run.started_on} → {run.ended_on}: {run.length} días</Badge>)}
                        {!(streakDetail.runs || []).length && <span className="text-sm text-slate-500">Sin runs.</span>}
                      </div>
                    </div>
                    <div>
                      <h4 className="mb-2 font-bold">Actividad acreditada ({streakDetail.events?.total || 0})</h4>
                      <div className="overflow-x-auto rounded-xl border">
                        <Table>
                          <TableHeader><TableRow><TableHead>Fecha local</TableHead><TableHead>Tipo</TableHead><TableHead>Fuente</TableHead><TableHead>Movimiento</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {(streakDetail.events?.items || []).map((event, index) => (
                              <TableRow key={`${event.source_id}-${index}`}>
                                <TableCell>{event.local_date}</TableCell>
                                <TableCell>{event.event_type}</TableCell>
                                <TableCell>{event.source_type}</TableCell>
                                <TableCell>
                                  {event.movement
                                    ? `${event.movement.currency || ""} ${Math.abs(Number(event.movement.amount || 0)).toLocaleString("es-CO")} · ${event.movement.description || event.movement.category || ""}`
                                    : (event.source_record?.name || "—")}
                                </TableCell>
                                <TableCell>{event.reversed_at ? <Badge variant="destructive">Revertido</Badge> : <Badge variant="outline">Activo</Badge>}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config" className="space-y-6">
            {/* App Settings Configuration */}
            <Card className="chart-container">
              <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-cyan-600" />
                  Configuración General
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <FreePlanSettings value={appSettings} onChange={setAppSettings} api={api}
                  preview={freeDurationPreview} onPreview={setFreeDurationPreview} />

                <div>
                  <label htmlFor="welcome-message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Primer mensaje para usuarios nuevos
                  </label>
                  <p id="welcome-message-help" className="text-xs text-slate-500 mb-3">
                    Personaliza la bienvenida del bot. Si lo dejas en blanco, se usará el mensaje predeterminado de Aorra como asistente financiero, con la pregunta sobre el primer gasto y el mini roast. Después continúa el flujo normal.
                  </p>
                  <textarea
                    id="welcome-message"
                    value={appSettings.welcome_message || ''}
                    onChange={(e) => setAppSettings({ ...appSettings, welcome_message: e.target.value })}
                    rows={8}
                    maxLength={3000}
                    placeholder="Déjalo en blanco para usar la bienvenida predeterminada."
                    aria-describedby="welcome-message-help"
                    className="w-full rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    data-testid="welcome-message-input"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    {(appSettings.welcome_message || '').length}/3000 caracteres. Guarda los cambios para aplicar el mensaje.
                  </p>
                </div>

                {/* Portal URL */}
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    URL del Portal
                  </p>
                  <p className="text-xs text-slate-500 mb-3">
                    Dominio de tu aplicación (usado para generar enlaces).
                  </p>
                  <Input
                    type="url"
                    placeholder="https://tu-dominio.com"
                    value={appSettings.portal_url}
                    onChange={(e) => setAppSettings({ ...appSettings, portal_url: e.target.value })}
                    className="max-w-md"
                    data-testid="portal-url-input"
                  />
                </div>
                
                {/* Bot Button URL */}
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-green-600" />
                    URL del Enlace de WhatsApp
                  </p>
                  <p className="text-xs text-slate-500 mb-3">
                    Esta URL se incluirá en los mensajes del bot como enlace de texto al portal.
                  </p>
                  <Input
                    type="url"
                    placeholder="https://tu-dominio.com/login"
                    value={appSettings.bot_button_url || ''}
                    onChange={(e) => setAppSettings({ ...appSettings, bot_button_url: e.target.value })}
                    className="max-w-md"
                    data-testid="bot-button-url-input"
                  />
                </div>

                {/* Feature Toggles */}
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700 mt-6">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-cyan-600" />
                    Visibilidad de Módulos (Feature Toggles)
                  </p>
                  <p className="text-xs text-slate-500 mb-4">
                    Desactiva los módulos que no quieres que estén disponibles para los usuarios en la aplicación. Esto lo ocultará del panel del usuario globalmente. Requerirá guardar cambios.
                  </p>
                  
                  <GameVisibilitySettings api={api} />
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { id: 'reminders', label: 'Recordatorios', icon: CalendarDays },
                      { id: 'habits', label: 'Hábitos', icon: Repeat },
                      { id: 'credit-cards', label: 'Tarjetas de Crédito', icon: Wallet },
                      { id: 'debts', label: 'Deudas', icon: CreditCard },
                      { id: 'subscriptions', label: 'Suscripciones', icon: Tv },
                      { id: 'savings', label: 'Metas Ahorro', icon: Target },
                      { id: 'assets', label: 'Activos', icon: TrendingUp },
                      { id: 'shared', label: 'Compartidos', icon: Users },
                      { id: 'trips', label: 'Viajes', icon: Plane },
                      { id: 'tableros', label: 'Tableros', icon: Users },
                      { id: 'planes', label: 'Mi Plan', icon: CreditCard }
                    ].map(module => {
                      const isActive = !(appSettings.hidden_modules || []).includes(module.id);
                      return (
                        <div key={module.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                          <div className="flex items-center gap-2">
                            <module.icon className={`w-4 h-4 ${isActive ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'}`} />
                            <span className={`text-sm font-medium ${isActive ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'}`}>
                              {module.label}
                            </span>
                          </div>
                          <Switch 
                            aria-label={`Activar ${module.label}`}
                            checked={isActive}
                            onCheckedChange={(checked) => toggleModule(module.id, checked)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Upcoming Features */}
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700 mt-6">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                    <Rocket className="w-4 h-4 text-cyan-600" />
                    Cajón de "Próximos Lanzamientos" (Mis Planes)
                  </p>
                  <p className="text-xs text-slate-500 mb-4">
                    Personaliza el contenido del cajón de próximos lanzamientos en la vista de Mis Planes. Las etiquetas deben ir separadas por comas.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Título</Label>
                      <Input
                        placeholder="Ej: Próximos lanzamientos 🚀"
                        value={appSettings.upcoming_features_title || ''}
                        onChange={(e) => setAppSettings({ ...appSettings, upcoming_features_title: e.target.value })}
                        className="max-w-md"
                      />
                    </div>
                    <div>
                      <Label>Descripción</Label>
                      <textarea
                        placeholder="Ej: Estamos preparando la integración directa..."
                        value={appSettings.upcoming_features_desc || ''}
                        onChange={(e) => setAppSettings({ ...appSettings, upcoming_features_desc: e.target.value })}
                        className="w-full min-h-[60px] max-w-md p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                      />
                    </div>
                    <div>
                      <Label>Etiquetas (separadas por comas)</Label>
                      <Input
                        placeholder="Ej: WEBHOOKS, MERCADOPAGO"
                        value={Array.isArray(appSettings.upcoming_features_tags) ? appSettings.upcoming_features_tags.join(', ') : (appSettings.upcoming_features_tags || '')}
                        onChange={(e) => {
                          const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                          setAppSettings({ ...appSettings, upcoming_features_tags: tags });
                        }}
                        className="max-w-md uppercase"
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <Button 
                  onClick={handleUpdateAppSettings}
                  disabled={settingsSubmitting}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="save-app-settings"
                >
                  {settingsSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Guardar Configuración
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="space-y-6">
            <WorkspaceNotificationSettings />
            {/* Meta/WhatsApp Credentials */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-['Outfit'] flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-500" />
                    Credenciales de WhatsApp / Meta
                  </CardTitle>
                  <Dialog open={metaDialogOpen} onOpenChange={setMetaDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Key className="w-4 h-4 mr-2" />
                        Editar Credenciales
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Configurar Credenciales de Meta</DialogTitle>
                        <DialogDescription>
                          Actualiza las credenciales de WhatsApp Business API
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div>
                          <Label>API Token (Access Token)</Label>
                          <Input
                            type="password"
                            value={metaCredentials.whatsapp_api_token}
                            onChange={(e) => setMetaCredentials({...metaCredentials, whatsapp_api_token: e.target.value})}
                            placeholder="EAAxxxxxxxx..."
                            className="font-mono text-xs"
                          />
                        </div>
                        <div>
                          <Label>Phone Number ID</Label>
                          <Input
                            value={metaCredentials.whatsapp_phone_id}
                            onChange={(e) => setMetaCredentials({...metaCredentials, whatsapp_phone_id: e.target.value})}
                            placeholder="965542023298887"
                            className="font-mono"
                          />
                        </div>
                        <div>
                          <Label>Verify Token (para webhook)</Label>
                          <Input
                            value={metaCredentials.whatsapp_verify_token}
                            onChange={(e) => setMetaCredentials({...metaCredentials, whatsapp_verify_token: e.target.value})}
                            placeholder="finchat_webhook_verify_2024"
                            className="font-mono"
                          />
                        </div>
                        <div>
                          <Label>Plantilla OTP</Label>
                          <Input
                            value={metaCredentials.whatsapp_otp_template}
                            onChange={(e) => setMetaCredentials({...metaCredentials, whatsapp_otp_template: e.target.value})}
                            placeholder="nombre_plantilla_otp"
                          />
                        </div>
                        <div>
                          <Label>Business Account ID</Label>
                          <Input
                            value={metaCredentials.whatsapp_business_account_id}
                            onChange={(e) => setMetaCredentials({...metaCredentials, whatsapp_business_account_id: e.target.value})}
                            placeholder="868121532492353"
                            className="font-mono"
                          />
                        </div>
                      </div>
                      <DialogFooter className="flex gap-2">
                        <Button 
                          variant="outline" 
                          onClick={handleTestMetaCredentials}
                          disabled={metaTesting}
                        >
                          {metaTesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                          Probar
                        </Button>
                        <Button 
                          onClick={handleUpdateMetaCredentials}
                          disabled={metaSubmitting}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {metaSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Guardar
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {/* Test Result */}
                {metaTestResult && (
                  <div className={`mb-4 p-4 rounded-lg ${metaTestResult.token_valid ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-rose-50 dark:bg-rose-900/20'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {metaTestResult.token_valid ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-rose-500" />
                      )}
                      <span className="font-medium">
                        {metaTestResult.token_valid ? 'Credenciales válidas' : 'Credenciales inválidas'}
                      </span>
                    </div>
                    {metaTestResult.phone_number && (
                      <p className="text-sm text-slate-600">Número: {metaTestResult.phone_number}</p>
                    )}
                  </div>
                )}

                {/* Current Config Display */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase font-bold">Phone ID</p>
                    <p className="font-mono text-sm">{metaCredentials.whatsapp_phone_id || 'No configurado'}</p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase font-bold">Plantilla OTP</p>
                    <p className="font-mono text-sm">{metaCredentials.whatsapp_otp_template || 'No configurado'}</p>
                  </div>
                </div>
                
                {/* Webhook URL */}
                <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    URL del Webhook de WhatsApp
                  </p>
                  <code className="text-xs bg-slate-200 dark:bg-slate-700 px-3 py-2 rounded block overflow-x-auto">
                    {process.env.REACT_APP_BACKEND_URL}/api/webhook/whatsapp
                  </code>
                  <p className="text-xs text-slate-500 mt-2">
                    Configura este endpoint en Meta Business Suite para recibir mensajes de WhatsApp.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Knowledge Base Section */}
            <Card className="chart-container">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-6 h-6 text-purple-600" />
                    <div>
                      <CardTitle className="font-['Outfit']">Base de Conocimientos</CardTitle>
                      <p className="text-xs text-slate-500 mt-1">
                        Información adicional que el bot usará para responder preguntas
                      </p>
                    </div>
                  </div>
                  
                  <Dialog open={kbDialogOpen} onOpenChange={setKbDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary" onClick={() => handleOpenKbDialog()} data-testid="add-kb-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Entrada
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="font-['Outfit']">
                          {kbEditId ? "Editar Entrada" : "Nueva Entrada"}
                        </DialogTitle>
                        <DialogDescription>Información que Aorra utilizará para responder consultas de los usuarios.</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleSaveKnowledgeEntry} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="kb-title">Título *</Label>
                          <Input
                            id="kb-title"
                            placeholder="Ej: Horarios de atención"
                            value={kbForm.title}
                            onChange={(e) => setKbForm({...kbForm, title: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="kb-category">Categoría</Label>
                          <Select 
                            value={kbForm.category} 
                            onValueChange={(v) => setKbForm({...kbForm, category: v})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona categoría" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">General</SelectItem>
                              <SelectItem value="servicios">Servicios</SelectItem>
                              <SelectItem value="precios">Precios</SelectItem>
                              <SelectItem value="faq">FAQs</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="kb-content">Contenido *</Label>
                          <textarea
                            id="kb-content"
                            placeholder="Escribe la información..."
                            value={kbForm.content}
                            onChange={(e) => setKbForm({...kbForm, content: e.target.value})}
                            className="w-full min-h-[100px] p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                          />
                        </div>
                        <Button type="submit" className="w-full btn-primary" disabled={kbSubmitting}>
                          {kbSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Guardar
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {knowledgeEntries.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No hay entradas en la base de conocimientos
                  </div>
                ) : (
                  <div className="space-y-3">
                    {knowledgeEntries.map((entry) => (
                      <div key={entry.id} className="p-4 border rounded-lg bg-slate-50 dark:bg-slate-800">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium">{entry.title}</h4>
                            <Badge variant="outline" className="mt-1 text-xs">{entry.category}</Badge>
                            <p className="text-sm text-slate-500 mt-2 line-clamp-2">{entry.content}</p>
                          </div>
                          <div className="flex gap-1 ml-4">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenKbDialog(entry)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-rose-500" onClick={() => handleDeleteKnowledgeEntry(entry.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backups Tab */}
          <TabsContent value="backups" className="space-y-6">
            <WorkspaceBackups users={users} />
            {/* Backup Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Backups</p>
                      <p className="text-2xl font-bold">{backupStats?.total_backups || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                      <RefreshCw className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Automáticos</p>
                      <p className="text-2xl font-bold">{backupStats?.by_type?.automatic || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                      <HardDrive className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Manuales</p>
                      <p className="text-2xl font-bold">{backupStats?.by_type?.manual || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                      <Users className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Usuarios con Backup</p>
                      <p className="text-2xl font-bold">{backupStats?.users_with_backups || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Backup Actions */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Sistema de Backups
                  </CardTitle>
                  <Button
                    onClick={handleRunAllBackups}
                    disabled={runningBackup}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {runningBackup ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Ejecutar Backup Masivo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="font-semibold mb-4 flex items-center gap-2">
                      <Settings className="w-4 h-4" /> Configuración Automática
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Activar Backups Automáticos</p>
                          <p className="text-xs text-muted-foreground">Ejecutar el sistema programado de copias de seguridad.</p>
                        </div>
                        <Switch
                          checked={backupSettings.enabled}
                          onCheckedChange={(checked) => setBackupSettings({...backupSettings, enabled: checked})}
                        />
                      </div>
                      
                      <div>
                        <p className="font-medium mb-1">Frecuencia de Backup (Días)</p>
                        <Input 
                          type="number" 
                          min="1"
                          value={backupSettings.interval_days}
                          onChange={(e) => setBackupSettings({...backupSettings, interval_days: parseInt(e.target.value) || 10})}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Requiere reiniciar el servidor si se cambia.</p>
                      </div>

                      <div>
                        <p className="font-medium mb-1">Retención de Backups (Días)</p>
                        <Input 
                          type="number" 
                          min="1"
                          value={backupSettings.retention_days}
                          onChange={(e) => setBackupSettings({...backupSettings, retention_days: parseInt(e.target.value) || 30})}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Tiempo antes de eliminar los backups antiguos.</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-center gap-3 mt-6">
                      <Button 
                        onClick={handleSaveBackupSettings} 
                        disabled={backupSettingsSubmitting}
                      >
                        {backupSettingsSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Guardar Configuración
                      </Button>
                      
                      <Button 
                        variant="destructive"
                        onClick={handleRunBackupCleanup}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Borrar backups antiguos ahora
                      </Button>
                    </div>

                    <ul className="text-sm space-y-2 text-muted-foreground mt-6 pt-4 border-t border-blue-200 dark:border-blue-800">
                      <li>• Último backup: <strong>{backupStats?.newest_backup ? formatDateTime(backupStats.newest_backup) : 'Ninguno'}</strong></li>
                      <li>• Backup más antiguo: <strong>{backupStats?.oldest_backup ? formatDateTime(backupStats.oldest_backup) : 'Ninguno'}</strong></li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <h4 className="font-semibold mb-2">⚠️ Importante</h4>
                    <p className="text-sm text-muted-foreground">
                      Para ver y restaurar backups de un usuario específico, ve a la pestaña <strong>Usuarios</strong> y 
                      haz clic en el botón <Database className="w-4 h-4 inline mx-1" /> junto al usuario.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Backups Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Información del Sistema
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-muted-foreground">Pre-Restauración</p>
                    <p className="text-xl font-bold">{backupStats?.by_type?.pre_restore || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-muted-foreground">Frecuencia</p>
                    <p className="text-lg font-bold">{backupStats?.backup_frequency || 'N/A'}</p>
                  </div>
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-muted-foreground">Retención</p>
                    <p className="text-lg font-bold">{backupStats?.retention_days || 30} días</p>
                  </div>
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <p className="text-xs text-muted-foreground">Estado</p>
                    <p className="text-lg font-bold text-green-600">Activo</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Backup Dialog for User */}
        <Dialog open={backupDialogOpen} onOpenChange={setBackupDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Backups de {selectedUserForBackup?.phone || 'Usuario'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {userBackups.length} backup(s) disponibles
                </p>
                <Button
                  size="sm"
                  onClick={() => handleCreateBackup(selectedUserForBackup?.id)}
                  disabled={backupLoading}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Crear Backup
                </Button>
              </div>
              
              {backupLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : userBackups.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay backups para este usuario</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {userBackups.map((backup) => (
                    <div
                      key={backup.backup_id}
                      className="p-3 border rounded-lg flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            backup.type === 'automatic' ? 'bg-blue-100 text-blue-700' :
                            backup.type === 'manual' ? 'bg-purple-100 text-purple-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {backup.type === 'automatic' ? 'Auto' : 
                             backup.type === 'manual' ? 'Manual' : 'Pre-Restore'}
                          </span>
                          <span className="text-sm font-medium">
                            {formatDateTime(backup.created_at)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Creado por: {backup.created_by || 'system'}
                          {backup.summary && (
                            <span className="ml-2">
                              • {backup.summary.transactions || 0} transacciones
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setBackupToRestore(backup);
                            setRestoreConfirmOpen(true);
                          }}
                          className="text-green-600 hover:text-green-700"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteBackup(backup.backup_id, backup.user_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Manage Subscription Modal */}
        <Dialog open={subModalOpen} onOpenChange={setSubModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-['Outfit'] text-indigo-600 dark:text-indigo-400">
                <CreditCard className="w-5 h-5" />
                Gestionar Suscripción
              </DialogTitle>
              <DialogDescription>
                Administra el plan de pago o configura el acceso gratuito para {subModalUser?.phone}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Mode Toggle */}
              <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => setSubModalMode("plan")}
                  className={`flex-1 text-xs py-2 rounded-md font-medium transition-all ${
                    subModalMode === "plan"
                      ? "bg-white dark:bg-slate-700 shadow-sm text-slate-850 dark:text-slate-100"
                      : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
                  }`}
                >
                  Asignar Plan
                </button>
                <button
                  type="button"
                  onClick={() => setSubModalMode("trial")}
                  className={`flex-1 text-xs py-2 rounded-md font-medium transition-all ${
                    subModalMode === "trial"
                      ? "bg-white dark:bg-slate-700 shadow-sm text-slate-850 dark:text-slate-100"
                      : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
                  }`}
                >
                  Acceso Gratis
                </button>
              </div>

              {subModalMode === "plan" ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Seleccionar Plan</Label>
                    <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                      <SelectTrigger className="w-full mt-1.5">
                        <SelectValue placeholder="Elige un plan..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clear">Sin Suscripción (Limpiar / Prueba)</SelectItem>
                          {plans && plans.filter(p => p.is_active).map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} ({p.price.toLocaleString('es-CO', { style: 'currency', currency: p.currency, maximumFractionDigits: 0 })} / {p.billing_period === 'monthly' ? 'Mensual' : p.billing_period === 'quarterly' ? 'Trimestral' : 'Anual'})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="p-3 bg-indigo-50/50 dark:bg-indigo-950/20 rounded-lg border border-indigo-100 dark:border-indigo-900 text-xs text-indigo-700 dark:text-indigo-300 space-y-1">
                    <p className="font-semibold flex items-center gap-1">
                      <Rocket className="w-3.5 h-3.5" /> Plan Manual
                    </p>
                    <p>
                      Al guardar, el estado se establecerá a "subscribed" y el tipo de suscripción como "manual". 
                      El vencimiento se calcula sumando 30 días para planes mensuales, 90 días para planes trimestrales o 365 días para planes anuales.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Días de acceso gratuito (0 = permanente)</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input
                        type="number"
                        min="0"
                        max="36500"
                        value={customTrialDays}
                        onChange={(e) => setCustomTrialDays(parseInt(e.target.value) || 0)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[0, 7, 14, 30, 90].map((days) => (
                      <Button
                        key={days}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCustomTrialDays(days)}
                        className="text-xs"
                      >
                        {days === 0 ? "Permanente" : `${days} días`}
                      </Button>
                    ))}
                  </div>
                  <div className="p-3 bg-amber-50/50 dark:bg-amber-950/20 rounded-lg border border-amber-100 dark:border-amber-900 text-xs text-amber-700 dark:text-amber-300 space-y-1">
                    <p className="font-semibold flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> Duración gratuita
                    </p>
                    <p>
                      Configura el acceso gratuito desde hoy; 0 significa permanente. Los beneficios de un pago vigente se conservan.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <div className="flex justify-end gap-2 w-full mt-2">
                <Button variant="outline" onClick={() => setSubModalOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveSubscription}
                  disabled={subModalSubmitting}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {subModalSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restore Confirmation Dialog */}
        <Dialog open={restoreConfirmOpen} onOpenChange={setRestoreConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <RotateCcw className="w-5 h-5" />
                Confirmar Restauración
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <p className="text-sm">
                  ¿Estás seguro de restaurar el backup del <strong>{formatDateTime(backupToRestore?.created_at)}</strong>?
                </p>
                <p className="text-sm mt-2 text-muted-foreground">
                  Esta acción reemplazará todos los datos actuales del usuario con los del backup.
                  Se creará un backup automático de los datos actuales antes de restaurar.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRestoreConfirmOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleRestoreBackup}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restaurar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminPage;
